import React, { useEffect, useMemo, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { Modal } from "react-bootstrap";
import moment from "moment";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  ArcElement,
  PointElement,
  Tooltip,
  BarElement,
} from "chart.js";
import {
  ApiDownload,
  BaseURL,
  ApiGet,
  ApiPatch,
  ApiPost,
} from "../../../helpers/API/ApiData";
import * as authUtil from "../../../utils/auth.util";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  buildOwnerInviteOptionsPayload,
  normalizeInviteFormForRole,
} from "./inviteOptions";
import "./EnterpriseOwners.scss";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend
);

const ENTERPRISE_DEFAULT = {
  enterpriseName: "",
  companyName: "",
  companyDomain: "",
  ownerName: "",
  ownerEmail: "",
  ownerPhone: "",
  ownerOrganizationName: "",
  ownerIndustry: "",
  ownerOrganizationSize: "",
  ownerGeographicScope: "",
  internalNotes: "",
  salesNotes: "",
  supportNotes: "",
  renewalNotes: "",
  planType: "enterprise-pilot",
  seatCount: 10,
  startDate: "",
  endDate: "",
  gracePeriod: 0,
  accountManager: "",
  prioritySupport: false,
  domainRestrictionEnabled: true,
  allowedEmailDomains: "",
  ssoRequired: false,
  manualApprovalRequired: false,
  autoExpiryEnabled: true,
  reminderNotificationsEnabled: true,
  seatThresholdAlertsEnabled: true,
  renewalRemindersEnabled: true,
  upgradeAvailable: true,
  downgradeAvailable: true,
  featureFlagRows: [{ key: "", enabled: true }],
  usageLimitRows: [{ key: "", value: "" }],
  ownerPermissions: {
    canAddUsers: true,
    canDeactivateUsers: true,
    canResendInvites: true,
    canViewUserActivity: true,
  },
  allowParticipantRole: true,
  allowParticipantOnly: false,
  defaultRoleMode: "owner_and_participant",
};

const INVITE_DEFAULT = {
  role: "enterprise_user",
  name: "",
  email: "",
  departmentFunction: "",
  leadershipLevel: "",
  relationshipToLeader: "",
  allowParticipantRole: true,
  allowParticipantOnly: false,
  defaultRoleMode: "owner_and_participant",
};
const OWNER_INDUSTRY_OPTIONS = [
  "Energy / Utilities",
  "Healthcare / Life Sciences",
  "Financial Services",
  "Technology",
  "Manufacturing",
  "Professional Services",
  "Government / Public Sector",
  "Education",
  "Other",
];
const OWNER_ORGANIZATION_SIZE_OPTIONS = [
  "Under 100",
  "100–499",
  "500–999",
  "1,000–4,999",
  "5,000–9,999",
  "10,000+",
];
const OWNER_GEOGRAPHIC_SCOPE_OPTIONS = ["Local", "Regional", "National", "Global"];
const DETAIL_TABS = [
  { key: "overview", label: "Overview" },
  { key: "users", label: "Participants" },
  { key: "verifications", label: "Verifications" },
  { key: "performance", label: "Performance" },
  { key: "logs", label: "Calibration Log" },
];
const VERIFICATION_STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "not_started", label: "Not started" },
  { value: "in_progress", label: "In progress" },
  { value: "skipped", label: "Skipped" },
  { value: "submitted", label: "Submitted" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];
const LOG_TABS = [
  { key: "requests", label: "Support Requests" },
  { key: "audit", label: "Audit Trail" },
  { key: "notifications", label: "Notification Events" },
  { key: "seat", label: "Seat Usage" },
  { key: "login", label: "Access Log" },
  { key: "challenge", label: "Calibration Sessions" },
];
const LOG_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const SHOW_ADVANCED_ENTERPRISE_CONFIG = false;
const SHOW_NOTES_SECTION = false;
const SHOW_ACCOUNT_MANAGER_FIELD = false;
const SHOW_PRIORITY_SUPPORT_FIELD = false;
const LOGO_ALLOWED_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp", ".svg"];
const LOGO_MAX_MB = 2;
const ROLE_LABELS = {
  enterprise_owner: "Enterprise Lead",
  enterprise_user: "Participant",
  internal_admin: "Admin",
};
const REQUEST_TYPE_LABELS = {
  request_more_seats: "Request more participant seats",
  request_renewal: "Request calibration window renewal",
  request_upgrade: "Request enterprise plan upgrade",
  contact_support: "Request support conversation",
};
const EVENT_LABELS = {
  enterprise_updated: "Enterprise configuration updated",
  enterprise_login: "Enterprise access login",
  enterprise_created: "Enterprise setup started",
  enterprise_activated: "Calibration window activated",
  enterprise_scheduled: "Calibration window scheduled",
  enterprise_deactivated: "Enterprise access disabled",
  enterprise_suspended: "Enterprise access restricted",
  enterprise_expired: "Calibration window closed",
  enterprise_renewed: "Calibration window renewed",
  owner_invited: "Enterprise Lead invited",
  owner_accepted: "Enterprise Lead invitation accepted",
  owner_changed: "Enterprise Lead assignment updated",
  user_invited: "Participant invitation sent",
  user_activated: "Participant access activated",
  user_deactivated: "Participant access deactivated",
  invite_resent: "Participant invitation resent",
  seat_changed: "Seat allocation updated",
  plan_changed: "Enterprise plan updated",
  features_changed: "Features changed",
  domain_rules_changed: "Domain rules changed",
  challenge_created: "Calibration session created",
  challenge_notification_sent: "Calibration invitations delivered",
  challenge_notification_failed: "Calibration invitation delivery failed",
  challenge_link_opened: "Calibration invite opened",
  challenge_joined: "Participant joined calibration",
  challenge_join_rejected: "Calibration join blocked",
  challenge_session_started: "Calibration session started",
  challenge_session_completed: "Calibration session completed",
};
const ENTERPRISE_STATUS_FILTERS = [
  { value: "all", label: "All access states" },
  { value: "active", label: "Active calibration" },
  { value: "scheduled", label: "Scheduled calibration" },
  { value: "draft", label: "Setup in progress" },
  { value: "inactive", label: "Access disabled" },
  { value: "suspended", label: "Temporarily restricted" },
  { value: "expired", label: "Calibration closed" },
];
const ENTERPRISE_DATE_FILTERS = [
  { value: "all", label: "All lifecycle dates" },
  { value: "upcoming_start", label: "Upcoming calibration start" },
  { value: "expiring_30", label: "Window closes in 30 days" },
  { value: "expired_date", label: "Window already closed" },
];

const STATUS_LABELS = {
  draft: "Setup in progress",
  scheduled: "Scheduled calibration",
  active: "Active calibration",
  inactive: "Access disabled",
  suspended: "Temporarily restricted",
  expired: "Calibration window closed",
  invited: "Invitation pending",
  revoked: "Invitation revoked",
  deactivated: "Access disabled",
};

const statusBadgeClass = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (["active", "approved", "sent"].includes(normalized)) return "badge-light-success";
  if (["suspended", "expired", "rejected", "failed"].includes(normalized)) {
    return "badge-light-danger";
  }
  if (["draft", "scheduled", "invited", "pending"].includes(normalized)) {
    return "badge-light-warning";
  }
  return "badge-light-primary";
};

const safeNumeric = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const toPercentDisplay = (value, digits = 2) => {
  if (value === null || value === undefined || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return `${number.toFixed(digits)}%`;
};

const toFixedDisplay = (value, digits = 2, suffix = "") => {
  if (value === null || value === undefined || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return `${number.toFixed(digits)}${suffix}`;
};

const secDisplay = (value) => {
  if (value === null || value === undefined) return "—";
  const totalSeconds = Number(value);
  if (!Number.isFinite(totalSeconds)) return "—";
  const sign = totalSeconds < 0 ? "-" : "";
  const safeSeconds = Math.abs(totalSeconds);
  if (safeSeconds < 60) {
    const precision = safeSeconds < 10 ? 1 : 0;
    return `${sign}${safeSeconds.toFixed(precision)} sec`;
  }
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = Math.floor(safeSeconds % 60);
  if (hours > 0) {
    return `${sign}${hours} hr ${String(minutes).padStart(2, "0")} min ${String(
      seconds
    ).padStart(2, "0")} sec`;
  }
  return `${sign}${minutes} min ${String(seconds).padStart(2, "0")} sec`;
};

const toRenderableText = (value, fallback = "—") => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string") {
    const text = value.trim();
    return text ? text : fallback;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (typeof value === "object") {
    if (
      Object.prototype.hasOwnProperty.call(value, "ciphertext") &&
      Object.prototype.hasOwnProperty.call(value, "iv")
    ) {
      return fallback;
    }
    const preferredKeys = ["name", "email", "value", "label", "title"];
    for (const key of preferredKeys) {
      const next = value?.[key];
      if (typeof next === "string" && next.trim()) return next.trim();
    }
  }
  return fallback;
};

const formatSessionDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return moment(date).format("MMM DD, YYYY HH:mm");
};

const formatSessionDuration = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  const totalSeconds = Math.max(0, Number(value));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const metricOrNA = (value, formatter = (v) => v) =>
  value === null || value === undefined ? "Data not available" : formatter(value);

const buildOwnerEventMetricCards = (eventMetrics = {}) => {
  const spread = eventMetrics?.interpretationSpread || {};
  const leaderBehavior = eventMetrics?.leaderBehavior || {};
  const decisionBehavior = eventMetrics?.decisionAdvancement || eventMetrics?.decisionBehavior || {};
  const participation9 = eventMetrics?.participation9 || {};
  const joinBehavior = eventMetrics?.joinBehavior || {};
  const timing = eventMetrics?.timing || {};
  const interpretation = eventMetrics?.interpretation || {};
  return [
    { label: "Interpretation Spread*", value: metricOrNA(spread.averageSpread, (v) => `${toFixedDisplay(v, 2)} (${safeNumeric(spread.cyclesWithSpread, 0)} cycles)`) },
    { label: "High Spread Cycles*", value: metricOrNA(spread.highSpreadPercent, (v) => `${toPercentDisplay(v)} (${safeNumeric(spread.highSpreadCycles, 0)}/${safeNumeric(spread.cyclesWithSpread, 0)})`) },
    { label: "Leader Adjustment Rate*", value: metricOrNA(leaderBehavior.adjustmentRatePercent, (v) => `${toPercentDisplay(v)} (${safeNumeric(leaderBehavior.matchedDecisions, 0)}/${safeNumeric(leaderBehavior.decisionsCompared, 0)})`) },
    { label: "Leader Independence Rate*", value: metricOrNA(leaderBehavior.independenceRatePercent, (v) => `${toPercentDisplay(v)} (${Math.max(0, safeNumeric(leaderBehavior.decisionsCompared, 0) - safeNumeric(leaderBehavior.matchedDecisions, 0))}/${safeNumeric(leaderBehavior.decisionsCompared, 0)})`) },
    { label: "Decision Advancement Rate*", value: metricOrNA(decisionBehavior.ratePercent, (v) => `${toPercentDisplay(v)} (${safeNumeric(decisionBehavior.advanceResponses, 0)}/${safeNumeric(decisionBehavior.totalResponses, 0)})`) },
    { label: "Participation (9 Events)*", value: metricOrNA(participation9.completionPercent, (v) => `${toPercentDisplay(v)} (${safeNumeric(participation9.completedEvents, 0)}/${safeNumeric(participation9.totalOpportunities, 0)})`) },
    { label: "Join Success Rate*", value: metricOrNA(joinBehavior.joinSuccessRatePercent, (v) => toPercentDisplay(v)) },
    { label: "Convergence*", value: metricOrNA(interpretation.convergencePercent, (v) => `${toPercentDisplay(v)} (${safeNumeric(spread.convergedCyclesCount, 0)} cycles)`) },
    { label: "Divergence*", value: metricOrNA(interpretation.divergencePercent, (v) => `${toPercentDisplay(v)} (${safeNumeric(spread.divergedCyclesCount, 0)} cycles)`) },
    { label: "Avg Leader Time*", value: metricOrNA(timing.avgLeaderTimeSeconds, (v) => secDisplay(v)) },
    { label: "Avg Peer Response Time*", value: metricOrNA(timing.avgPeerResponseTimeSeconds, (v) => secDisplay(v)) },
    { label: "Avg Reinforce Time*", value: metricOrNA(timing.avgReinforceTimeSeconds, (v) => secDisplay(v)) },
  ];
};

const buildOwnerMetricCountCards = (eventMetrics = {}) => {
  const spread = eventMetrics?.interpretationSpread || {};
  const leader = eventMetrics?.leaderBehavior || {};
  const decision = eventMetrics?.decisionAdvancement || eventMetrics?.decisionBehavior || {};
  const participation9 = eventMetrics?.participation9 || {};
  const join = eventMetrics?.joinBehavior || {};
  const timing = eventMetrics?.timing || {};
  return [
    { label: "Participation Completed / Opportunities", value: `${safeNumeric(participation9.completedEvents, 0)} / ${safeNumeric(participation9.totalOpportunities, 0)}` },
    { label: "Join (Invited / Joined / Missed / Incomplete)", value: `${safeNumeric(join.invited, 0)} / ${safeNumeric(join.joinedInTime, 0)} / ${safeNumeric(join.missed, 0)} / ${safeNumeric(join.joinedButIncomplete, 0)}` },
    { label: "Spread Cycles (Total / Converged / Diverged)", value: `${safeNumeric(spread.cyclesWithSpread, 0)} / ${safeNumeric(spread.convergedCyclesCount, 0)} / ${safeNumeric(spread.divergedCyclesCount, 0)}` },
    { label: "Decision Responses (Advance / Not Advance / Total)", value: `${safeNumeric(decision.advanceResponses, 0)} / ${Math.max(0, safeNumeric(decision.totalResponses, 0) - safeNumeric(decision.advanceResponses, 0))} / ${safeNumeric(decision.totalResponses, 0)}` },
    { label: "Leader Next-Move Adoption (Matched / Ignored / Compared)", value: `${safeNumeric(leader.matchedDecisions, 0)} / ${Math.max(0, safeNumeric(leader.decisionsCompared, 0) - safeNumeric(leader.matchedDecisions, 0))} / ${safeNumeric(leader.decisionsCompared, 0)}` },
    { label: "Timing Samples (Leader / Peer / Reinforce)", value: `${safeNumeric(timing.leaderTimeSampleCount, 0)} / ${safeNumeric(timing.peerResponseSampleCount, 0)} / ${safeNumeric(timing.reinforceTimeSampleCount, 0)}` },
  ];
};

const asPercentValue = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return number <= 1 ? number * 100 : number;
};

const resolveParticipantUserId = (participant) =>
  participant?.userId
  || participant?._id
  || participant?.user?._id
  || participant?.profile?.userId
  || participant?.id
  || "";

const chartPalette = {
  blue: "#1d4ed8",
  blueSoft: "rgba(29, 78, 216, 0.18)",
  green: "#15803d",
  greenSoft: "rgba(21, 128, 61, 0.2)",
  yellow: "#b7791f",
  yellowSoft: "rgba(183, 121, 31, 0.24)",
  red: "#be123c",
  cyan: "#0f766e",
};

const baseLineOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "top" },
    tooltip: { mode: "index", intersect: false },
  },
  interaction: { mode: "index", intersect: false },
  scales: {
    y: {
      beginAtZero: true,
      ticks: { color: "#526181", font: { size: 11 } },
      grid: { color: "rgba(82, 97, 129, 0.14)" },
    },
    x: {
      ticks: { color: "#526181", font: { size: 11 } },
      grid: { color: "rgba(82, 97, 129, 0.08)" },
    },
  },
};

const getInitials = (primary = "", fallback = "") => {
  const value = String(primary || fallback || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!value.length) return "NA";
  if (value.length === 1) return value[0].slice(0, 2).toUpperCase();
  return `${value[0][0] || ""}${value[1][0] || ""}`.toUpperCase();
};

const getAuthHeaders = () => {
  const token = authUtil.getToken() || authUtil.getAdminToken() || "";
  return {
    Authorization: token ? `Bearer ${token}` : "",
    api_key: "6QSy49rUTH",
  };
};

const formatDate = (value, format = "MMM DD, YYYY") => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return moment(date).format(format);
};

const toDisplayText = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const toStatusText = (value = "") =>
  STATUS_LABELS[String(value || "").toLowerCase()] || toDisplayText(value || "unknown");
const isLikelyUrl = (value = "") => /^https?:\/\//i.test(String(value || "").trim());

const isOwnerVerificationRow = (row = {}, enterprise = null) => {
  const ownerUserId = String(
    enterprise?.ownerProfile?.userId || enterprise?.ownerProfile?._id || ""
  ).trim();
  const ownerEmail = String(enterprise?.ownerProfile?.email || "")
    .trim()
    .toLowerCase();
  const rowUserId = String(row?.user?._id || row?.userId || "").trim();
  const rowEmail = String(row?.user?.email || "")
    .trim()
    .toLowerCase();
  const membershipRole = String(
    row?.user?.membershipRole || row?.membershipRole || ""
  )
    .trim()
    .toLowerCase();

  if (ownerUserId && rowUserId && ownerUserId === rowUserId) return true;
  if (ownerEmail && rowEmail && ownerEmail === rowEmail) return true;
  return membershipRole === "enterprise_owner";
};

const formatSimpleValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    return value
      .map((item) => formatSimpleValue(item))
      .filter(Boolean)
      .join(", ");
  }
  return "";
};

const summarizeObject = (object = {}) => {
  if (!object || typeof object !== "object") return "";
  const lines = [];
  const skipKeys = [
    "enterpriseSnapshot",
    "templateMetadata",
    "metadata",
    "challengeId",
    "roomId",
    "invitationId",
    "token",
  ];

  Object.entries(object).forEach(([key, value]) => {
    if (skipKeys.includes(String(key || ""))) {
      return;
    }

    const normalized = formatSimpleValue(value);
    if (!normalized) return;
    lines.push(`${toDisplayText(key)}: ${normalized}`);
  });

  return lines.slice(0, 3).join(" • ");
};

const getEventLabel = (row = {}, tab = "audit") => {
  if (tab === "requests") return "Enterprise Lead support request submitted";
  if (tab === "seat") return "Seat usage snapshot";
  if (row.entityType === "owner_request") return "Enterprise Lead support request submitted";
  const event = String(row.eventType || row.source || row.note || row.channel || "entry");
  return EVENT_LABELS[event] || toDisplayText(event);
};

const getNotificationDetails = (row = {}) => {
  const metadata =
    row?.metadata?.templateMetadata && typeof row.metadata.templateMetadata === "object"
      ? row.metadata.templateMetadata
      : row?.metadata || {};

  const parts = [];
  if (metadata?.summary) parts.push(metadata.summary);
  if (metadata?.challengeName) parts.push(`Calibration: ${metadata.challengeName}`);
  if (metadata?.totalRecipients) parts.push(`Participants notified: ${metadata.totalRecipients}`);
  if (metadata?.seatUsage) parts.push(`Seat utilization: ${metadata.seatUsage}`);
  if (row.status === "failed" && row.error) parts.push(`Failed: ${row.error}`);
  if (row.status === "skipped" && (metadata?.reason || row.error)) {
    parts.push(`Skipped: ${metadata?.reason || row.error}`);
  }

  return parts.join(" • ") || "Notification event recorded.";
};

const getOwnerRequestDetails = (row = {}) => {
  const details = row.details || {};
  const requestType = String(details.requestType || "").toLowerCase();
  const requestLabel =
    REQUEST_TYPE_LABELS[requestType] || toDisplayText(requestType || "request");
  const note = String(details.note || "").trim();

  if (note) {
    return `${requestLabel} • ${note}`;
  }
  return requestLabel;
};

const getLoginDetails = (row = {}) => {
  const details = row.details || {};
  const role =
    ROLE_LABELS[String(details.role || "").toLowerCase()] || toDisplayText(details.role || "participant");
  const status = toStatusText(details.enterpriseStatus || "unknown");
  const view = toDisplayText(details.view || "dashboard");

  return `${role} login • Access State: ${status} • View: ${view}`;
};

const getChallengeDetails = (row = {}) => {
  const details = row.details || {};
  const challengeName = details.challengeName || "Calibration session";
  const eventType = String(row.eventType || "").toLowerCase();

  if (eventType === "challenge_created") return `${challengeName} created`;
  if (eventType === "challenge_notification_sent") {
    const count = details.totalRecipients || details.recipients || "";
    return count ? `Participant invitation sent to ${count}` : "Participant invitation sent";
  }
  if (eventType === "challenge_notification_failed") return "Calibration notification failed";
  if (eventType === "challenge_link_opened") return "Invitation opened";
  if (eventType === "challenge_joined") return `Participant joined ${challengeName}`;
  if (eventType === "challenge_join_rejected") {
    const reason = toDisplayText(details.reason || "not authorized");
    return `Join blocked • ${reason}`;
  }
  if (eventType === "challenge_session_started") return `${challengeName} started`;
  if (eventType === "challenge_session_completed") return `${challengeName} completed`;
  if (details.status) return `Status: ${toStatusText(details.status)}`;
  return `${challengeName} activity`;
};

const getLogUserLabel = (row = {}, tab = "audit") => {
  if (tab === "notifications") {
    return row.recipientEmail || "—";
  }

  if (tab === "seat") {
    return toDisplayText(row.source || "system");
  }

  return row.actorName || row.actorEmail || row.recipientEmail || "System";
};

const getLogDetailsText = (row = {}, tab = "audit") => {
  if (tab === "seat") {
    return [
      `Active: ${row.activeUsers || 0}`,
      `Invited: ${row.invitedUsers || 0}`,
      `Seats: ${row.seatCount || 0}`,
    ]
      .filter(Boolean)
      .join(" • ");
  }

  if (tab === "requests") {
    return getOwnerRequestDetails(row);
  }

  if (tab === "notifications") {
    return getNotificationDetails(row);
  }

  if (tab === "login" || row.eventType === "enterprise_login") {
    return getLoginDetails(row);
  }

  if (tab === "challenge" || String(row.eventType || "").toLowerCase().startsWith("challenge_")) {
    return getChallengeDetails(row);
  }

  if (row.entityType === "owner_request") {
    return getOwnerRequestDetails(row);
  }

  if (typeof row.details === "string" && row.details.trim()) {
    return row.details.trim();
  }

  const summaryFromDetails = summarizeObject(row.details || {});
  if (summaryFromDetails) return summaryFromDetails;

  const summaryFromMetadata = summarizeObject(row.metadata || {});
  if (summaryFromMetadata) return summaryFromMetadata;

  return "No additional context";
};

const toDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const normalizeDomainInput = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .split("/")[0];

const getApiErrorPayload = (error = {}) =>
  error?.payload || error?.response?.data?.payload || error?.data?.payload || {};

const getApiErrorMessage = (error = {}, fallback = "Request failed.") =>
  error?.message ||
  error?.response?.data?.message ||
  error?.data?.message ||
  fallback;

const getApiErrorField = (error = {}) => getApiErrorPayload(error)?.field || "";

const normalizeEmailValue = (value = "") => String(value || "").trim().toLowerCase();

const membershipHasRole = (membership = {}, role = "") => {
  const normalizedRole = String(role || "").toLowerCase();
  const roles = Array.isArray(membership.roles) ? membership.roles : [];
  return (
    String(membership.role || "").toLowerCase() === normalizedRole ||
    roles.map((item) => String(item || "").toLowerCase()).includes(normalizedRole)
  );
};

const DOMAIN_REGEX =
  /^(?=.{1,253}$)(?!-)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;

const isValidDomain = (value = "") => DOMAIN_REGEX.test(String(value || "").trim());
const validateDomainFields = (formState = {}, options = {}) => {
  const { validateAllowedDomains = true } = options;
  const errors = {};
  const companyDomainInput = String(formState.companyDomain || "").trim();
  const companyDomain = normalizeDomainInput(companyDomainInput);
  const allowedDomains = String(formState.allowedEmailDomains || "")
    .split(",")
    .map((part) => normalizeDomainInput(part))
    .filter(Boolean);

  if (!companyDomainInput) {
    errors.companyDomain = "Company domain is required.";
  } else if (!isValidDomain(companyDomain)) {
    errors.companyDomain =
      "Enter a valid company domain (for example: leaderbridge.com).";
  }

  if (validateAllowedDomains) {
    const invalidAllowed = allowedDomains.filter((domain) => !isValidDomain(domain));
    if (invalidAllowed.length) {
      errors.allowedEmailDomains = `Invalid domain: ${invalidAllowed[0]}. Use values like lexa.com.`;
    }
  }

  return errors;
};

const objectToFeatureFlagRows = (value = {}) => {
  const entries = Object.entries(value || {}).filter(([key]) => String(key || "").trim());
  if (!entries.length) return [{ key: "", enabled: true }];
  return entries.map(([key, flagValue]) => ({
    key: String(key || "").trim(),
    enabled: Boolean(flagValue),
  }));
};

const objectToUsageLimitRows = (value = {}) => {
  const entries = Object.entries(value || {}).filter(([key]) => String(key || "").trim());
  if (!entries.length) return [{ key: "", value: "" }];
  return entries.map(([key, limitValue]) => ({
    key: String(key || "").trim(),
    value:
      limitValue === null || limitValue === undefined ? "" : String(limitValue),
  }));
};

const rowsToFeatureFlags = (rows = []) =>
  (rows || []).reduce((acc, item) => {
    const key = String(item?.key || "").trim();
    if (!key) return acc;
    acc[key] = Boolean(item?.enabled);
    return acc;
  }, {});

const rowsToUsageLimits = (rows = []) =>
  (rows || []).reduce((acc, item) => {
    const key = String(item?.key || "").trim();
    if (!key) return acc;
    const rawValue = String(item?.value || "").trim();
    if (!rawValue) return acc;
    const parsedNumber = Number(rawValue);
    acc[key] = Number.isNaN(parsedNumber) ? rawValue : parsedNumber;
    return acc;
  }, {});

const buildEnterpriseForm = (enterprise) => {
  if (!enterprise) return { ...ENTERPRISE_DEFAULT };

  return {
    enterpriseName: enterprise?.name || "",
    companyName: enterprise?.companyName || "",
    companyDomain: enterprise?.companyDomain || "",
    ownerName: enterprise?.ownerProfile?.name || "",
    ownerEmail: enterprise?.ownerProfile?.email || "",
    ownerPhone: enterprise?.ownerProfile?.phone || "",
    ownerOrganizationName:
      enterprise?.ownerProfile?.organizationName ||
      enterprise?.companyName ||
      "",
    ownerIndustry: enterprise?.ownerProfile?.industry || "",
    ownerOrganizationSize: enterprise?.ownerProfile?.organizationSize || "",
    ownerGeographicScope: enterprise?.ownerProfile?.geographicScope || "",
    internalNotes: enterprise?.notes?.internal || "",
    salesNotes: enterprise?.notes?.sales || "",
    supportNotes: enterprise?.notes?.support || "",
    renewalNotes: enterprise?.notes?.renewal || "",
    planType: enterprise?.plan?.type || "enterprise-pilot",
    seatCount: Number(enterprise?.plan?.seatCount || 0) || 1,
    startDate: toDateInput(enterprise?.lifecycle?.startDate),
    endDate: toDateInput(enterprise?.lifecycle?.endDate),
    gracePeriod: Number(enterprise?.lifecycle?.gracePeriodDays || 0),
    accountManager: enterprise?.accountManager || "",
    prioritySupport: Boolean(enterprise?.prioritySupport),
    domainRestrictionEnabled:
      enterprise?.accessRules?.domainRestrictionEnabled !== false,
    allowedEmailDomains: (() => {
      const allowed = (enterprise?.accessRules?.allowedEmailDomains || [])
        .map((domain) => normalizeDomainInput(domain))
        .filter(Boolean);
      if (allowed.length) return allowed.join(",");
      return normalizeDomainInput(enterprise?.companyDomain || "");
    })(),
    ssoRequired: Boolean(enterprise?.accessRules?.ssoRequired),
    manualApprovalRequired: Boolean(enterprise?.accessRules?.manualApprovalRequired),
    autoExpiryEnabled:
      enterprise?.lifecycle?.autoExpiryEnabled !== false,
    reminderNotificationsEnabled:
      enterprise?.lifecycle?.reminderNotificationsEnabled !== false,
    seatThresholdAlertsEnabled:
      enterprise?.lifecycle?.seatThresholdAlertsEnabled !== false,
    renewalRemindersEnabled:
      enterprise?.lifecycle?.renewalRemindersEnabled !== false,
    upgradeAvailable: enterprise?.lifecycle?.upgradeAvailable !== false,
    downgradeAvailable: enterprise?.lifecycle?.downgradeAvailable !== false,
    featureFlagRows: objectToFeatureFlagRows(enterprise?.featureFlags || {}),
    usageLimitRows: objectToUsageLimitRows(enterprise?.plan?.usageLimits || {}),
    ownerPermissions: {
      canAddUsers: enterprise?.ownerPermissions?.canAddUsers !== false,
      canDeactivateUsers: enterprise?.ownerPermissions?.canDeactivateUsers !== false,
      canResendInvites: enterprise?.ownerPermissions?.canResendInvites !== false,
      canViewUserActivity: enterprise?.ownerPermissions?.canViewUserActivity !== false,
    },
  };
};

const TableSkeleton = () => (
  <div className="enterprise-owners__tableSkeleton">
    {[1, 2, 3, 4, 5, 6].map((row) => (
      <div key={row} className="enterprise-owners__tableSkeletonRow">
        <span className="enterprise-owners__tableSkeletonCell enterprise-owners__tableSkeletonCell--lg enterprise-owners__tableSkeletonCell--toneA" />
        <span className="enterprise-owners__tableSkeletonCell enterprise-owners__tableSkeletonCell--md enterprise-owners__tableSkeletonCell--toneB" />
        <span className="enterprise-owners__tableSkeletonCell enterprise-owners__tableSkeletonCell--sm enterprise-owners__tableSkeletonCell--toneC" />
      </div>
    ))}
  </div>
);

const EnterpriseOwners = () => {
  const [enterprises, setEnterprises] = useState([]);
  const [search, setSearch] = useState("");
  const [enterpriseStatusFilter, setEnterpriseStatusFilter] = useState("all");
  const [enterprisePlanFilter, setEnterprisePlanFilter] = useState("all");
  const [enterpriseDateFilter, setEnterpriseDateFilter] = useState("all");
  const [loadingTable, setLoadingTable] = useState(false);
  const [enterprisesError, setEnterprisesError] = useState("");
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const [enterpriseModalOpen, setEnterpriseModalOpen] = useState(false);
  const [enterpriseSubmitting, setEnterpriseSubmitting] = useState(false);
  const [enterpriseForm, setEnterpriseForm] = useState({ ...ENTERPRISE_DEFAULT });
  const [enterpriseFormErrors, setEnterpriseFormErrors] = useState({});

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsEnterpriseId, setDetailsEnterpriseId] = useState(null);
  const [detailsEnterprise, setDetailsEnterprise] = useState(null);
  const [detailsForm, setDetailsForm] = useState({ ...ENTERPRISE_DEFAULT });
  const [detailsFormErrors, setDetailsFormErrors] = useState({});
  const [detailTab, setDetailTab] = useState("overview");
  const [verificationRows, setVerificationRows] = useState([]);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationError, setVerificationError] = useState("");
  const [verificationStatusFilter, setVerificationStatusFilter] = useState("all");
  const [selectedVerificationId, setSelectedVerificationId] = useState("");
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [verificationPreview, setVerificationPreview] = useState(null);
  const [verificationActionLoading, setVerificationActionLoading] = useState(false);
  const [verificationDecisionReason, setVerificationDecisionReason] = useState("");
  const [detailLogTab, setDetailLogTab] = useState("audit");
  const [memberships, setMemberships] = useState([]);
  const [seatUsage, setSeatUsage] = useState(null);
  const [detailLogRows, setDetailLogRows] = useState([]);
  const [detailLogsLoading, setDetailLogsLoading] = useState(false);
  const [detailLogsError, setDetailLogsError] = useState("");
  const [detailLogPagination, setDetailLogPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [inviteForm, setInviteForm] = useState({ ...INVITE_DEFAULT });
  const [transferOwnership, setTransferOwnership] = useState({
    membershipId: "",
    ownerName: "",
    ownerEmail: "",
  });
  const [transferOwnershipError, setTransferOwnershipError] = useState("");
  const [transferOwnershipSubmitting, setTransferOwnershipSubmitting] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [performanceWindow, setPerformanceWindow] = useState("30d");
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [performanceExporting, setPerformanceExporting] = useState(false);
  const [performanceRebuilding, setPerformanceRebuilding] = useState(false);
  const [performanceGlobalRebuilding, setPerformanceGlobalRebuilding] = useState(false);
  const [performanceError, setPerformanceError] = useState("");
  const [performanceSummary, setPerformanceSummary] = useState(null);
  const [performanceReport, setPerformanceReport] = useState(null);
  const [performanceSessions, setPerformanceSessions] = useState([]);
  const [performanceLastRebuiltAt, setPerformanceLastRebuiltAt] = useState("");
  const [selectedPerformanceUserId, setSelectedPerformanceUserId] = useState("");
  const [selectedPerformanceUserName, setSelectedPerformanceUserName] = useState("");
  const [participantPerformanceLoading, setParticipantPerformanceLoading] = useState(false);
  const [participantPerformanceExporting, setParticipantPerformanceExporting] = useState(false);
  const [participantPerformanceData, setParticipantPerformanceData] = useState(null);
  const [participantPerformanceError, setParticipantPerformanceError] = useState("");
  const [participantSessionsPdfLoading, setParticipantSessionsPdfLoading] = useState(false);
  const [participantAllSessionsLoading, setParticipantAllSessionsLoading] = useState(false);
  const [participantAllSessionsError, setParticipantAllSessionsError] = useState("");
  const [participantAllSessions, setParticipantAllSessions] = useState([]);
  const [participantExpandedSessionId, setParticipantExpandedSessionId] = useState("");
  const [participantSessionMatrixCache, setParticipantSessionMatrixCache] = useState({});
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [selectedLogoPreviewUrl, setSelectedLogoPreviewUrl] = useState("");
  const [logoImageError, setLogoImageError] = useState(false);
  const logoInputRef = useRef(null);
  const detailsWorkspaceRef = useRef(null);
  const participantReportRef = useRef(null);

  const [rowAction, setRowAction] = useState({ id: null, action: "" });

  const currentOwnerEmail = normalizeEmailValue(detailsEnterprise?.ownerProfile?.email || "");
  const transferTargetOptions = useMemo(
    () =>
      (memberships || [])
        .filter((membership) => {
          const email = normalizeEmailValue(membership?.emailLower || membership?.email || "");
          const status = String(membership?.status || "").toLowerCase();
          if (!email || email === currentOwnerEmail) return false;
          if (!["active", "invited"].includes(status)) return false;
          return !membershipHasRole(membership, "enterprise_owner");
        })
        .map((membership) => ({
          id: String(membership?._id || ""),
          name: String(membership?.name || "").trim(),
          email: normalizeEmailValue(membership?.emailLower || membership?.email || ""),
          status: String(membership?.status || "").toLowerCase(),
        }))
        .filter((membership) => membership.id && membership.email),
    [memberships, currentOwnerEmail]
  );
  const customStyles = {
    table: {
      style: {
        backgroundColor: "#ffffff",
      },
    },
    headRow: {
      style: {
        minHeight: "50px",
        borderTop: "none",
        borderBottom: "1px solid #e4e6ef",
        background: "#f5f7fb",
      },
    },
    headCells: {
      style: {
        color: "#5e6278",
        fontSize: "11px",
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        paddingLeft: "16px",
        paddingRight: "16px",
        lineHeight: "1",
        borderRight: "1px solid #eceff5",
        "&:last-of-type": {
          borderRight: "none",
        },
      },
    },
    rows: {
      style: {
        minHeight: "62px",
        borderBottom: "1px solid #edf0f6",
        "&:hover": {
          backgroundColor: "#f8fafe",
        },
      },
    },
    cells: {
      style: {
        paddingLeft: "14px",
        paddingRight: "14px",
        color: "#3f4254",
        fontSize: "12.5px",
      },
    },
    pagination: {
      style: {
        borderTop: "1px solid #dbe7fb",
        backgroundColor: "#f8fbff",
        minHeight: "62px",
        paddingLeft: "10px",
        paddingRight: "10px",
        fontSize: "12px",
        color: "#375179",
      },
    },
  };
  const paginationComponentOptions = {
    rowsPerPageText: "Rows per page",
    rangeSeparatorText: "of",
    noRowsPerPage: false,
    selectAllRowsItem: false,
  };

  const loadEnterprises = async () => {
    try {
      const res = await ApiGet("admin/enterprises?limit=300");
      const next = res?.data?.payload?.enterprises || [];
      setEnterprises(next);
      setEnterprisesError("");
      return true;
    } catch (error) {
      const message = error?.message || "Unable to load enterprise records.";
      setEnterprisesError(message);
      toast.error(message);
      return false;
    }
  };

  const refreshTables = async () => {
    setLoadingTable(true);
    const enterprisesLoaded = await loadEnterprises();
    if (enterprisesLoaded) {
      setLastRefreshedAt(new Date());
    }
    setLoadingTable(false);
  };

  useEffect(() => {
    refreshTables();
  }, []);

  const filteredEnterprises = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = new Date();
    const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    return enterprises.filter((item) => {
      const itemStatus = String(item.status || "").toLowerCase();
      const itemPlan = String(item?.plan?.type || "").toLowerCase();
      const startDate = item?.lifecycle?.startDate
        ? new Date(item.lifecycle.startDate)
        : null;
      const endDate = item?.lifecycle?.endDate ? new Date(item.lifecycle.endDate) : null;

      const statusMatch =
        enterpriseStatusFilter === "all" ? true : itemStatus === enterpriseStatusFilter;
      if (!statusMatch) return false;

      const planMatch =
        enterprisePlanFilter === "all"
          ? true
          : itemPlan === String(enterprisePlanFilter || "").toLowerCase();
      if (!planMatch) return false;

      let dateMatch = true;
      if (enterpriseDateFilter === "upcoming_start") {
        dateMatch = Boolean(startDate && startDate > now);
      } else if (enterpriseDateFilter === "expiring_30") {
        dateMatch = Boolean(endDate && endDate >= now && endDate <= in30Days);
      } else if (enterpriseDateFilter === "expired_date") {
        dateMatch = Boolean(endDate && endDate < now);
      }
      if (!dateMatch) return false;

      if (!q) return true;

      return (
        String(item.name || "").toLowerCase().includes(q) ||
        String(item.companyName || "").toLowerCase().includes(q) ||
        String(item.companyDomain || "").toLowerCase().includes(q) ||
        String(item.ownerProfile?.email || "").toLowerCase().includes(q) ||
        String(item.status || "").toLowerCase().includes(q)
      );
    });
  }, [
    enterprises,
    search,
    enterpriseStatusFilter,
    enterprisePlanFilter,
    enterpriseDateFilter,
  ]);

  const enterprisePlanOptions = useMemo(() => {
    const plans = Array.from(
      new Set(
        enterprises
          .map((item) => String(item?.plan?.type || "").trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));

    return [{ value: "all", label: "All plans" }].concat(
      plans.map((plan) => ({ value: plan, label: plan }))
    );
  }, [enterprises]);

  const activeData = filteredEnterprises;
  const hasEnterpriseFilters =
    search.trim().length > 0 ||
    enterpriseStatusFilter !== "all" ||
    enterprisePlanFilter !== "all" ||
    enterpriseDateFilter !== "all";
  const hasActiveFilters = hasEnterpriseFilters;
  const activeTotalCount = enterprises.length;
  const activeTableError = enterprisesError;

  const handleEnterpriseFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setEnterpriseForm((prev) => {
      const next = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "companyDomain") {
        const normalized = normalizeDomainInput(value);
        next.allowedEmailDomains = normalized;
      }

      return next;
    });
    setEnterpriseFormErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleDetailsFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setDetailsForm((prev) => {
      const next = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "companyDomain") {
        const normalized = normalizeDomainInput(value);
        const currentAllowed = String(prev.allowedEmailDomains || "").trim();
        if (!currentAllowed) {
          next.allowedEmailDomains = normalized;
        }
      }

      return next;
    });
    setDetailsFormErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleOwnerPermissionChange = (key, checked) => {
    setDetailsForm((prev) => ({
      ...prev,
      ownerPermissions: {
        ...(prev.ownerPermissions || {}),
        [key]: checked,
      },
    }));
  };

  const openCreateEnterprise = () => {
    setEnterpriseForm({
      ...ENTERPRISE_DEFAULT,
      startDate: toDateInput(new Date()),
    });
    setEnterpriseFormErrors({});
    setEnterpriseModalOpen(true);
  };

  const validateCreateEnterpriseForm = (formState = {}) => {
    const errors = {};
    const enterpriseName = String(formState.enterpriseName || "").trim();
    const planType = String(formState.planType || "").trim();
    const ownerEmail = String(formState.ownerEmail || "").trim();
    const seatCount = Number(formState.seatCount || 0);
    const startDate = String(formState.startDate || "").trim();
    const endDate = String(formState.endDate || "").trim();

    if (!enterpriseName) errors.enterpriseName = "Enterprise name is required.";
    if (!planType) errors.planType = "Plan type is required.";
    if (!startDate) errors.startDate = "Start date is required.";
    if (!endDate) errors.endDate = "End date is required.";
    if (!ownerEmail) {
      errors.ownerEmail = "Enterprise Lead email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownerEmail)) {
      errors.ownerEmail = "Enter a valid Enterprise Lead email address.";
    }
    if (!String(formState.ownerOrganizationName || "").trim()) {
      errors.ownerOrganizationName = "Organization name is required.";
    }

    if (!Number.isFinite(seatCount) || seatCount < 1) {
      errors.seatCount = "Seat count must be at least 1.";
    }

    if (startDate && endDate) {
      const startAt = new Date(startDate);
      const endAt = new Date(endDate);
      if (!Number.isNaN(startAt.getTime()) && !Number.isNaN(endAt.getTime()) && endAt < startAt) {
        errors.endDate = "End date cannot be earlier than start date.";
      }
    }

    Object.assign(errors, validateDomainFields(formState, { validateAllowedDomains: false }));

    return errors;
  };

  const buildEnterprisePayload = (formState) => {
    const featureFlags = rowsToFeatureFlags(formState.featureFlagRows || []);
    const usageLimits = rowsToUsageLimits(formState.usageLimitRows || []);
    const normalizedCompanyDomain = normalizeDomainInput(formState.companyDomain || "");
    const allowedEmailDomains = String(formState.allowedEmailDomains || "")
      .split(",")
      .map((part) => normalizeDomainInput(part))
      .filter(Boolean);

    return {
      payload: {
        enterpriseName: formState.enterpriseName.trim(),
        companyName: formState.companyName.trim() || formState.enterpriseName.trim(),
        companyDomain: formState.companyDomain.trim(),
        ownerName: formState.ownerName.trim(),
        ownerEmail: formState.ownerEmail.trim(),
        ownerPhone: formState.ownerPhone.trim(),
        enterpriseSponsorProfile: {
          organizationName: formState.ownerOrganizationName.trim(),
          industry: formState.ownerIndustry || "",
          organizationSize: formState.ownerOrganizationSize || "",
          geographicScope: formState.ownerGeographicScope || "",
        },
        internalNotes: formState.internalNotes,
        salesNotes: formState.salesNotes,
        supportNotes: formState.supportNotes,
        renewalNotes: formState.renewalNotes,
        planType: formState.planType.trim() || "enterprise-pilot",
        seatCount: Number(formState.seatCount || 1),
        startDate: formState.startDate || null,
        endDate: formState.endDate || null,
        gracePeriod: Number(formState.gracePeriod || 0),
        featureFlags,
        usageLimits,
        accountManager: formState.accountManager,
        prioritySupport: Boolean(formState.prioritySupport),
        ownerPermissions: {
          ...(formState.ownerPermissions || {}),
        },
        domainRestrictionEnabled: Boolean(formState.domainRestrictionEnabled),
        allowedEmailDomains:
          allowedEmailDomains.length > 0
            ? allowedEmailDomains
            : normalizedCompanyDomain
              ? [normalizedCompanyDomain]
              : [],
        ssoRequired: Boolean(formState.ssoRequired),
        manualApprovalRequired: Boolean(formState.manualApprovalRequired),
        autoExpiryEnabled: Boolean(formState.autoExpiryEnabled),
        reminderNotificationsEnabled: Boolean(formState.reminderNotificationsEnabled),
        seatThresholdAlertsEnabled: Boolean(formState.seatThresholdAlertsEnabled),
        renewalRemindersEnabled: Boolean(formState.renewalRemindersEnabled),
        upgradeAvailable: Boolean(formState.upgradeAvailable),
        downgradeAvailable: Boolean(formState.downgradeAvailable),
        ownerInviteOptions: {
          ...buildOwnerInviteOptionsPayload(formState),
        },
      },
    };
  };

  const updateFeatureFlagRow = (mode, index, field, value) => {
    const updater = (prev) => {
      const next = [...(prev.featureFlagRows || [])];
      if (mode === "add") {
        next.push({ key: "", enabled: true });
      } else if (mode === "remove") {
        next.splice(index, 1);
      } else if (mode === "change" && next[index]) {
        next[index] = {
          ...next[index],
          [field]: field === "enabled" ? Boolean(value) : value,
        };
      }
      return {
        ...prev,
        featureFlagRows: next.length ? next : [{ key: "", enabled: true }],
      };
    };

    if (detailsOpen) {
      setDetailsForm(updater);
    } else {
      setEnterpriseForm(updater);
    }
  };

  const updateUsageLimitRow = (mode, index, field, value) => {
    const updater = (prev) => {
      const next = [...(prev.usageLimitRows || [])];
      if (mode === "add") {
        next.push({ key: "", value: "" });
      } else if (mode === "remove") {
        next.splice(index, 1);
      } else if (mode === "change" && next[index]) {
        next[index] = {
          ...next[index],
          [field]: value,
        };
      }
      return {
        ...prev,
        usageLimitRows: next.length ? next : [{ key: "", value: "" }],
      };
    };

    if (detailsOpen) {
      setDetailsForm(updater);
    } else {
      setEnterpriseForm(updater);
    }
  };

  const submitEnterprise = async (event) => {
    event.preventDefault();

    const errors = validateCreateEnterpriseForm(enterpriseForm);
    setEnterpriseFormErrors(errors);
    if (Object.keys(errors).length) {
      toast.error("Please fix required fields before creating the enterprise.");
      return;
    }

    const built = buildEnterprisePayload(enterpriseForm);
    if (built.error) {
      toast.error(built.error);
      return;
    }

    setEnterpriseSubmitting(true);
    try {
      await ApiPost("admin/enterprises", built.payload);
      toast.success("Enterprise created and Enterprise Lead invitation sent.");
      setEnterpriseModalOpen(false);
      await loadEnterprises();
    } catch (error) {
      const message = getApiErrorMessage(error, "Unable to create enterprise.");
      if (getApiErrorField(error) === "ownerEmail") {
        setEnterpriseFormErrors((prev) => ({ ...prev, ownerEmail: message }));
      }
      toast.error(message);
    } finally {
      setEnterpriseSubmitting(false);
    }
  };

  const loadEnterpriseDetails = async (enterpriseId) => {
    setDetailsLoading(true);
    try {
      const detailsRes = await ApiGet(`admin/enterprises/${enterpriseId}`);

      const payload = detailsRes?.data?.payload || {};
      const enterprise = payload.enterprise || null;

      setDetailsEnterprise(enterprise);
      setDetailsEnterpriseId(enterpriseId);
      setMemberships(payload.memberships || []);
      setSeatUsage(payload.seatUsage || null);
      setDetailLogRows([]);
      setDetailLogsError("");
      setDetailLogPagination((prev) => ({ ...prev, page: 1, total: 0, totalPages: 1 }));
      setVerificationRows([]);
      setVerificationError("");
      setVerificationStatusFilter("all");
      setSelectedVerification(null);
      setSelectedVerificationId("");
      setVerificationPreview(null);
      setVerificationDecisionReason("");
      setDetailsFormErrors({});

      setDetailsForm(buildEnterpriseForm(enterprise));
      setTransferOwnership({
        membershipId: "",
        ownerName: "",
        ownerEmail: "",
      });
      setTransferOwnershipError("");
      setInviteForm({ ...INVITE_DEFAULT });
    } catch (error) {
      toast.error("Unable to load enterprise details.");
    } finally {
      setDetailsLoading(false);
    }
  };

  const openDetails = async (enterprise) => {
    setDetailsOpen(true);
    setDetailTab("overview");
    setDetailLogTab("audit");
    await loadEnterpriseDetails(enterprise?._id);
  };

  useEffect(() => {
    if (!detailsOpen) return;
    if (!detailsWorkspaceRef.current) return;
    detailsWorkspaceRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [detailsOpen, detailsEnterpriseId]);

  const buildDetailLogRequest = ({ enterpriseId, tab, page, limit }) => {
    const base = `admin/enterprises/${enterpriseId}`;
    const query = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });

    if (tab === "requests") {
      query.set("scope", "requests");
      return `${base}/audit-logs?${query.toString()}`;
    }
    if (tab === "challenge") {
      query.set("scope", "challenge");
      return `${base}/audit-logs?${query.toString()}`;
    }
    if (tab === "notifications") return `${base}/notification-logs?${query.toString()}`;
    if (tab === "seat") return `${base}/seat-usage?${query.toString()}`;
    if (tab === "login") return `${base}/login-history?${query.toString()}`;
    return `${base}/audit-logs?${query.toString()}`;
  };

  const loadDetailLogs = async ({
    enterpriseId = detailsEnterpriseId,
    tab = detailLogTab,
    page = detailLogPagination.page,
    limit = detailLogPagination.limit,
  } = {}) => {
    if (!enterpriseId) return;
    setDetailLogsLoading(true);
    setDetailLogsError("");
    try {
      const endpoint = buildDetailLogRequest({ enterpriseId, tab, page, limit });
      const res = await ApiGet(endpoint);
      const payload = res?.data?.payload || {};
      const nextRows = payload.logs || payload.history || [];

      setDetailLogRows(nextRows);
      setDetailLogPagination({
        page: Number(payload.page || page || 1),
        limit: Number(payload.limit || limit || 10),
        total: Number(payload.total || 0),
        totalPages: Math.max(Number(payload.totalPages || 1), 1),
      });
    } catch (error) {
      setDetailLogRows([]);
      setDetailLogsError(error?.message || "Unable to load activity log.");
    } finally {
      setDetailLogsLoading(false);
    }
  };

  const buildPerformanceQuery = () => {
    const query = new URLSearchParams();
    if (performanceWindow && performanceWindow !== "all") {
      query.set("window", performanceWindow);
    }
    return query.toString();
  };

  const loadPerformance = async ({ enterpriseId = detailsEnterpriseId } = {}) => {
    if (!enterpriseId) return;
    setPerformanceLoading(true);
    setPerformanceError("");
    try {
      const query = buildPerformanceQuery();
      const suffix = query ? `?${query}` : "";
      const [summaryRes, sessionsRes] = await Promise.all([
        ApiGet(`admin/performance/enterprise/${enterpriseId}${suffix}`),
        ApiGet(`admin/performance/enterprise/${enterpriseId}/sessions${suffix}`),
      ]);
      setPerformanceSummary(summaryRes?.data?.payload?.summary || null);
      setPerformanceReport(summaryRes?.data?.payload?.report || null);
      setPerformanceSessions(sessionsRes?.data?.payload?.sessions || []);
    } catch (error) {
      setPerformanceSummary(null);
      setPerformanceReport(null);
      setPerformanceSessions([]);
      setPerformanceError(error?.message || "Unable to load performance analytics.");
    } finally {
      setPerformanceLoading(false);
    }
  };

  const exportPerformancePdf = async () => {
    if (!detailsEnterpriseId) return;
    setPerformanceExporting(true);
    try {
      const query = buildPerformanceQuery();
      const endpoint = `admin/performance/enterprise/${detailsEnterpriseId}/export-pdf${
        query ? `?${query}` : ""
      }`;
      const blob = await ApiDownload(endpoint);
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `enterprise-performance-${detailsEnterpriseId}-${Date.now()}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Performance report prepared.");
    } catch (error) {
      toast.error(error?.message || "Unable to export performance report.");
    } finally {
      setPerformanceExporting(false);
    }
  };

  const loadParticipantAllSessions = async (resolvedUserId) => {
    if (!resolvedUserId) return;
    setParticipantAllSessionsLoading(true);
    setParticipantAllSessionsError("");
    try {
      const query = new URLSearchParams({ enterpriseId: detailsEnterpriseId });
      if (performanceWindow && performanceWindow !== "all") {
        query.set("window", performanceWindow);
      }
      const response = await ApiGet(
        `admin/performance/user/${resolvedUserId}/sessions?${query.toString()}`
      );
      const sessions = response?.data?.payload?.sessions || [];
      setParticipantAllSessions(Array.isArray(sessions) ? sessions : []);
    } catch (error) {
      setParticipantAllSessions([]);
      setParticipantAllSessionsError(error?.message || "Unable to load participant sessions.");
    } finally {
      setParticipantAllSessionsLoading(false);
    }
  };

  const downloadParticipantSessionsPdf = async () => {
    if (!selectedPerformanceUserId || !detailsEnterpriseId) return;
    setParticipantSessionsPdfLoading(true);
    setParticipantAllSessionsError("");
    try {
      const query = new URLSearchParams({ enterpriseId: detailsEnterpriseId });
      if (performanceWindow && performanceWindow !== "all") {
        query.set("window", performanceWindow);
      }
      const blob = await ApiDownload(
        `admin/performance/user/${selectedPerformanceUserId}/sessions/export-pdf?${query.toString()}`
      );
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `admin-participant-show-all-sessions-${selectedPerformanceUserId}-${Date.now()}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setParticipantAllSessionsError(error?.message || "Unable to download sessions report.");
    } finally {
      setParticipantSessionsPdfLoading(false);
    }
  };

  const getParticipantSessionMatrixDetail = (roomId) =>
    participantSessionMatrixCache[String(roomId || "")] || null;

  const loadParticipantSessionMatrix = async ({ userId, roomId }) => {
    const safeUserId = String(userId || "");
    const safeRoomId = String(roomId || "");
    if (!safeUserId || !safeRoomId || !detailsEnterpriseId) return;
    const existing = participantSessionMatrixCache[safeRoomId];
    if (existing?.status === "loading" || existing?.status === "loaded") return;
    setParticipantSessionMatrixCache((prev) => ({
      ...prev,
      [safeRoomId]: {
        status: "loading",
        rows: [],
        maxPeerColumns: 0,
        error: "",
      },
    }));
    try {
      const query = new URLSearchParams({ enterpriseId: detailsEnterpriseId, roomId: safeRoomId });
      if (performanceWindow && performanceWindow !== "all") {
        query.set("window", performanceWindow);
      }
      const response = await ApiGet(
        `admin/performance/user/${safeUserId}/sessions?${query.toString()}`
      );
      const payload = response?.data?.payload || {};
      const matrixRows = Array.isArray(payload?.matrixRows) ? payload.matrixRows : [];
      const maxPeerColumns = Math.max(
        Number(payload?.maxPeerColumns || 0),
        matrixRows.reduce(
          (max, row) => Math.max(max, Array.isArray(row?.peers) ? row.peers.length : 0),
          0
        )
      );
      setParticipantSessionMatrixCache((prev) => ({
        ...prev,
        [safeRoomId]: {
          status: "loaded",
          rows: matrixRows,
          maxPeerColumns,
          error: "",
        },
      }));
    } catch (error) {
      setParticipantSessionMatrixCache((prev) => ({
        ...prev,
        [safeRoomId]: {
          status: "error",
          rows: [],
          maxPeerColumns: 0,
          error: error?.message || "Unable to load this session detail.",
        },
      }));
    }
  };

  const toggleParticipantSessionExpansion = ({ userId, roomId }) => {
    const safeRoomId = String(roomId || "");
    if (!safeRoomId) return;
    setParticipantExpandedSessionId((current) => (current === safeRoomId ? "" : safeRoomId));
    if (participantExpandedSessionId !== safeRoomId) {
      loadParticipantSessionMatrix({ userId, roomId: safeRoomId });
    }
  };

  const loadParticipantPerformance = async (participantOrUserId) => {
    const resolvedUserId = typeof participantOrUserId === "object"
      ? resolveParticipantUserId(participantOrUserId)
      : participantOrUserId;
    const resolvedUserName =
      typeof participantOrUserId === "object"
        ? String(participantOrUserId?.user || participantOrUserId?.name || "").trim()
        : "";

    if (!detailsEnterpriseId || !resolvedUserId) {
      setParticipantPerformanceError("Unable to open drilldown: participant identifier is missing.");
      return;
    }

    setSelectedPerformanceUserId(String(resolvedUserId));
    if (resolvedUserName) {
      setSelectedPerformanceUserName(resolvedUserName);
    }
    setParticipantPerformanceError("");
    setParticipantPerformanceLoading(true);
    setParticipantExpandedSessionId("");
    setParticipantSessionMatrixCache({});
    setParticipantAllSessions([]);
    setParticipantAllSessionsError("");
    if (participantReportRef.current) {
      participantReportRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    try {
      const query = new URLSearchParams({ enterpriseId: detailsEnterpriseId });
      if (performanceWindow && performanceWindow !== "all") {
        query.set("window", performanceWindow);
      }
      const response = await ApiGet(
        `admin/performance/user/${resolvedUserId}?${query.toString()}`
      );
      setParticipantPerformanceData(response?.data?.payload || null);
      await loadParticipantAllSessions(String(resolvedUserId));
      if (participantReportRef.current) {
        participantReportRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } catch (error) {
      setParticipantPerformanceData(null);
      setParticipantAllSessions([]);
      setParticipantPerformanceError(error?.message || "Unable to load participant performance.");
      toast.error(error?.message || "Unable to load participant performance.");
    } finally {
      setParticipantPerformanceLoading(false);
    }
  };

  const exportParticipantPerformancePdf = async () => {
    if (!selectedPerformanceUserId || !detailsEnterpriseId) return;
    setParticipantPerformanceExporting(true);
    try {
      const query = new URLSearchParams({ enterpriseId: detailsEnterpriseId });
      if (performanceWindow && performanceWindow !== "all") {
        query.set("window", performanceWindow);
      }
      const blob = await ApiDownload(
        `admin/performance/user/${selectedPerformanceUserId}/export-pdf?${query.toString()}`
      );
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `participant-performance-${selectedPerformanceUserId}-${Date.now()}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.message || "Unable to export participant performance report.");
    } finally {
      setParticipantPerformanceExporting(false);
    }
  };

  const rebuildPerformanceSnapshots = async () => {
    if (!detailsEnterpriseId) return;
    setPerformanceRebuilding(true);
    try {
      const response = await ApiPost(
        `admin/performance/enterprise/${detailsEnterpriseId}/rebuild`,
        {
          maxSessions: 1500,
        }
      );
      const rebuilt = Number(response?.data?.payload?.snapshotsRebuilt || 0);
      toast.success(`Performance snapshots rebuilt (${rebuilt}).`);
      setPerformanceLastRebuiltAt(new Date().toISOString());
      await loadPerformance({ enterpriseId: detailsEnterpriseId });
    } catch (error) {
      toast.error(error?.message || "Unable to rebuild performance snapshots.");
    } finally {
      setPerformanceRebuilding(false);
    }
  };

  const rebuildAllPerformanceSnapshots = async () => {
    setPerformanceGlobalRebuilding(true);
    try {
      const response = await ApiPost("admin/performance/rebuild-all", {
        maxSessionsPerEnterprise: 1200,
      });
      const rebuilt = Number(response?.data?.payload?.enterprisesRebuilt || 0);
      toast.success(`Global performance rebuild complete (${rebuilt} enterprises).`);
      setPerformanceLastRebuiltAt(new Date().toISOString());
      if (detailsEnterpriseId) {
        await loadPerformance({ enterpriseId: detailsEnterpriseId });
      }
    } catch (error) {
      toast.error(error?.message || "Unable to rebuild all enterprise performance snapshots.");
    } finally {
      setPerformanceGlobalRebuilding(false);
    }
  };

  const saveEnterpriseChanges = async () => {
    if (!detailsEnterpriseId) return;

    const formErrors = validateDomainFields(detailsForm);
    if (!String(detailsForm.ownerOrganizationName || "").trim()) {
      formErrors.ownerOrganizationName = "Organization name is required.";
    }
    setDetailsFormErrors(formErrors);
    if (Object.keys(formErrors).length) {
      toast.error("Please fix invalid domain fields before saving.");
      return;
    }

    const built = buildEnterprisePayload({
      ...detailsForm,
      ownerEmail: detailsEnterprise?.ownerProfile?.email || detailsForm.ownerEmail,
    });
    if (built.error) {
      toast.error(built.error);
      return;
    }

    setDetailsLoading(true);
    try {
      await ApiPatch(`admin/enterprises/${detailsEnterpriseId}`, built.payload);
      toast.success("Enterprise configuration saved.");
      await Promise.all([loadEnterprises(), loadEnterpriseDetails(detailsEnterpriseId)]);
    } catch (error) {
      const message = getApiErrorMessage(error, "Unable to save enterprise changes.");
      if (getApiErrorField(error) === "ownerEmail") {
        setDetailsFormErrors((prev) => ({ ...prev, ownerEmail: message }));
      }
      toast.error(message);
    } finally {
      setDetailsLoading(false);
    }
  };

  const clearSelectedEnterpriseLogo = () => {
    if (selectedLogoPreviewUrl) {
      window.URL.revokeObjectURL(selectedLogoPreviewUrl);
    }
    setSelectedLogoFile(null);
    setSelectedLogoPreviewUrl("");
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  };

  const handleEnterpriseLogoFileSelect = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    if (selectedLogoPreviewUrl) {
      window.URL.revokeObjectURL(selectedLogoPreviewUrl);
    }

    const localPreview = window.URL.createObjectURL(file);
    setSelectedLogoFile(file);
    setSelectedLogoPreviewUrl(localPreview);
  };

  const saveEnterpriseLogoByAdmin = async () => {
    if (!detailsEnterpriseId || !selectedLogoFile) return;

    setLogoBusy(true);
    try {
      const body = new FormData();
      body.append("logo", selectedLogoFile);
      const response = await fetch(
        `${BaseURL}admin/enterprises/${detailsEnterpriseId}/logo`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body,
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data?.error?.message || data?.message || "Unable to upload enterprise logo."
        );
      }

      toast.success("Enterprise logo updated.");
      clearSelectedEnterpriseLogo();
      await Promise.all([loadEnterprises(), loadEnterpriseDetails(detailsEnterpriseId)]);
    } catch (error) {
      toast.error(error?.message || "Unable to upload enterprise logo.");
    } finally {
      setLogoBusy(false);
    }
  };

  const removeEnterpriseLogoByAdmin = async () => {
    if (!detailsEnterpriseId) return;
    if (!window.confirm("Remove current enterprise logo?")) return;

    setLogoBusy(true);
    try {
      const response = await fetch(
        `${BaseURL}admin/enterprises/${detailsEnterpriseId}/logo`,
        {
          method: "DELETE",
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data?.error?.message || data?.message || "Unable to remove enterprise logo."
        );
      }

      toast.success("Enterprise logo removed.");
      await Promise.all([loadEnterprises(), loadEnterpriseDetails(detailsEnterpriseId)]);
    } catch (error) {
      toast.error(error?.message || "Unable to remove enterprise logo.");
    } finally {
      setLogoBusy(false);
    }
  };

  useEffect(() => () => {
    if (selectedLogoPreviewUrl) {
      window.URL.revokeObjectURL(selectedLogoPreviewUrl);
    }
  }, [selectedLogoPreviewUrl]);

  useEffect(() => {
    clearSelectedEnterpriseLogo();
  }, [detailsEnterpriseId]);

  useEffect(() => {
    setLogoImageError(false);
  }, [detailsEnterprise?.logoUrl, detailsEnterpriseId]);

  const runEnterpriseAction = async (action, extra = {}) => {
    if (!detailsEnterpriseId) return;

    const confirmMap = {
      deactivate: "Deactivate this enterprise?",
      suspend: "Suspend this enterprise?",
      mark_expired: "Mark this enterprise as expired?",
      decrease_seats: "Reduce seat count?",
      remove_invite: "Remove this invite permanently?",
    };

    if (confirmMap[action] && !window.confirm(confirmMap[action])) {
      return;
    }

    setDetailsLoading(true);
    try {
      await ApiPost(`admin/enterprises/${detailsEnterpriseId}/action`, {
        action,
        ...extra,
      });
      toast.success("Action completed.");
      await Promise.all([loadEnterprises(), loadEnterpriseDetails(detailsEnterpriseId)]);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Action could not be completed."));
    } finally {
      setDetailsLoading(false);
    }
  };

  const sendInvite = async () => {
    if (!detailsEnterpriseId) return;
    if (!inviteForm.email.trim()) {
      toast.error("Participant email is required.");
      return;
    }

    setDetailsLoading(true);
    try {
      const ownerInviteOptions = buildOwnerInviteOptionsPayload(inviteForm);
      await ApiPost(`admin/enterprises/${detailsEnterpriseId}/invite`, {
        role: inviteForm.role,
        name: inviteForm.name.trim(),
        email: inviteForm.email.trim(),
        participantProfile: {
          departmentFunction: inviteForm.departmentFunction || "",
          leadershipLevel: inviteForm.leadershipLevel || "",
          relationshipToLeader: inviteForm.relationshipToLeader || "",
        },
        allowParticipantRole:
          inviteForm.role === "enterprise_owner"
            ? ownerInviteOptions.allowParticipantRole
            : false,
        allowParticipantOnly:
          inviteForm.role === "enterprise_owner"
            ? ownerInviteOptions.allowParticipantOnly
            : false,
        defaultRoleMode:
          inviteForm.role === "enterprise_owner"
            ? ownerInviteOptions.defaultRoleMode
            : "participant_only",
      });
      toast.success("Participant invitation sent.");
      setInviteForm({ ...INVITE_DEFAULT });
      await loadEnterpriseDetails(detailsEnterpriseId);
    } catch (error) {
      toast.error(error?.message || "Unable to send invite.");
    } finally {
      setDetailsLoading(false);
    }
  };

  const manageMembership = async (membership, action) => {
    if (!detailsEnterpriseId || !membership?._id) return;

    if (
      ["deactivate_user", "remove_invite", "cancel_invite"].includes(action) &&
      !window.confirm("Please confirm this action.")
    ) {
      return;
    }

    setDetailsLoading(true);
    try {
      await ApiPost(
        `admin/enterprises/${detailsEnterpriseId}/users/${membership._id}/action`,
        { action }
      );
      toast.success("Membership updated.");
      await loadEnterpriseDetails(detailsEnterpriseId);
    } catch (error) {
      toast.error(error?.message || "Unable to update membership.");
    } finally {
      setDetailsLoading(false);
    }
  };

  const submitOwnershipTransfer = async () => {
    const selected = transferTargetOptions.find(
      (item) =>
        normalizeEmailValue(item.email) === normalizeEmailValue(transferOwnership.ownerEmail || "")
    );
    const selectedEmail = normalizeEmailValue(selected?.email || transferOwnership.ownerEmail || "");

    if (!selectedEmail) {
      setTransferOwnershipError("Enter a valid Enterprise Lead email address.");
      return;
    }

    if (selectedEmail === currentOwnerEmail) {
      setTransferOwnershipError("This person is already the Enterprise Lead.");
      return;
    }

    setTransferOwnershipError("");
    setTransferOwnershipSubmitting(true);
    try {
      await ApiPost(`admin/enterprises/${detailsEnterpriseId}/action`, {
        action: "transfer_ownership",
        membershipId: selected?.id || undefined,
        ownerName: transferOwnership.ownerName.trim() || selected?.name || "",
        ownerEmail: selectedEmail,
      });
      toast.success("Enterprise Lead transferred.");
      setTransferOwnership({ membershipId: "", ownerName: "", ownerEmail: "" });
      await Promise.all([loadEnterprises(), loadEnterpriseDetails(detailsEnterpriseId)]);
    } catch (error) {
      const message = getApiErrorMessage(error, "Unable to transfer Enterprise Lead.");
      setTransferOwnershipError(message);
      toast.error(message);
    } finally {
      setTransferOwnershipSubmitting(false);
    }
  };

  const exportUsers = async () => {
    if (!detailsEnterpriseId) return;
    setDetailsLoading(true);
    try {
      const blob = await ApiDownload(`admin/enterprises/${detailsEnterpriseId}/export/users`);
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `enterprise-users-${detailsEnterpriseId}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      toast.success("User export prepared.");
    } catch (error) {
      toast.error("Unable to export users.");
    } finally {
      setDetailsLoading(false);
    }
  };

  const exportLogs = async (logType = detailLogTab) => {
    if (!detailsEnterpriseId) return;
    setDetailsLoading(true);
    try {
      const blob = await ApiDownload(
        `admin/enterprises/${detailsEnterpriseId}/export/logs?type=${encodeURIComponent(
          String(logType || "audit")
        )}`
      );
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `enterprise-${detailsEnterpriseId}-${logType}-logs.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Activity log export prepared.");
    } catch (error) {
      toast.error("Unable to export activity log.");
    } finally {
      setDetailsLoading(false);
    }
  };

  const getEnterpriseToggleAction = (enterprise) => {
    const isActive = String(enterprise?.status || "").toLowerCase() === "active";
    return {
      action: isActive ? "deactivate" : "activate",
      label: isActive ? "Deactivate" : "Activate",
      className: isActive
        ? "enterprise-owners__actionBtn enterprise-owners__actionBtn--danger"
        : "enterprise-owners__actionBtn enterprise-owners__actionBtn--success",
    };
  };

  const enterpriseColumns = [
    {
      name: "Enterprise",
      cell: (row) => (
        <div className="enterprise-owners__identityPrimary">
          {toRenderableText(row?.name, toRenderableText(row?.companyName, "Unnamed enterprise"))}
        </div>
      ),
      sortable: true,
      wrap: false,
      grow: 1.5,
    },
    {
      name: "Enterprise Lead",
      cell: (row) => (
        <div className="enterprise-owners__identityStack">
          <div className="enterprise-owners__identityPrimary">
            {toRenderableText(row?.ownerProfile?.name, "Enterprise Lead not assigned")}
          </div>
          <div className="enterprise-owners__identityHint">
            {toRenderableText(row?.ownerProfile?.email, "Email not available")}
          </div>
        </div>
      ),
      wrap: false,
      minWidth: "230px",
      grow: 1.1,
    },
    {
      name: "Lifecycle Status",
      cell: (row) => (
        <span className={`badge ${statusBadgeClass(row.status)}`}>
          {toStatusText(row.status || "draft")}
        </span>
      ),
      sortable: true,
      width: "140px",
    },
    {
      name: "Enterprise Plan",
      cell: (row) => (
        <span className="enterprise-owners__planBadge">{row?.plan?.type || "plan-not-set"}</span>
      ),
      grow: 0.9,
    },
    {
      name: "Last Updated",
      cell: (row) => (
        <strong>{formatDate(row?.updatedAt || row?.createdAt)}</strong>
      ),
      sortable: true,
      wrap: false,
      grow: 0.8,
    },
    {
      name: "Actions",
      allowOverflow: true,
      button: true,
      ignoreRowClick: true,
      minWidth: "200px",
      cell: (row) => (
        <div className="enterprise-owners__actions enterprise-owners__actions--row">
          <button
            type="button"
            className="enterprise-owners__actionBtn enterprise-owners__actionBtn--primary"
            onClick={() => openDetails(row)}
          >
            Open
          </button>
          {(() => {
            const toggleAction = getEnterpriseToggleAction(row);
            return (
              <button
                type="button"
                className={toggleAction.className}
                onClick={() => runEnterpriseActionFromTable(row, toggleAction.action)}
                disabled={rowAction.id === row._id}
              >
                {toggleAction.label}
              </button>
            );
          })()}
        </div>
      ),
      grow: 1.2,
    },
  ];

  const runEnterpriseActionFromTable = async (enterprise, action) => {
    setRowAction({ id: enterprise?._id, action });
    try {
      await ApiPost(`admin/enterprises/${enterprise._id}/action`, { action });
      toast.success("Action completed.");
      await loadEnterprises();
    } catch (error) {
      toast.error(error?.message || "Action could not be completed.");
    } finally {
      setRowAction({ id: null, action: "" });
    }
  };

  const enterpriseStats = useMemo(() => {
    const now = new Date();
    const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const next = {
      total: enterprises.length,
      active: 0,
      scheduled: 0,
      attention: 0,
      expiringSoon: 0,
    };

    enterprises.forEach((item) => {
      const status = String(item.status || "").toLowerCase();
      if (status === "active") next.active += 1;
      if (status === "scheduled") next.scheduled += 1;
      if (["inactive", "suspended", "expired"].includes(status)) {
        next.attention += 1;
      }

      const endDate = item?.lifecycle?.endDate ? new Date(item.lifecycle.endDate) : null;
      if (endDate && endDate >= now && endDate <= in30Days) next.expiringSoon += 1;
    });

    return next;
  }, [enterprises]);

  const summaryCards = [
    { label: "Total Enterprises", value: enterpriseStats.total },
    { label: "Active Window", value: enterpriseStats.active },
    { label: "Scheduled", value: enterpriseStats.scheduled },
    { label: "Needs Attention", value: enterpriseStats.attention + enterpriseStats.expiringSoon },
  ];

  const clearFilters = () => {
    setSearch("");
    setEnterpriseStatusFilter("all");
    setEnterprisePlanFilter("all");
    setEnterpriseDateFilter("all");
  };

  const tableState = useMemo(() => {
    if (activeTableError) {
      return {
        tone: "error",
        title: "Unable to load records",
        description: activeTableError,
        actionLabel: "Retry",
      };
    }

    if (hasActiveFilters) {
      return {
        tone: "neutral",
        title: "No enterprise matches these filters",
        description: "Adjust search or lifecycle filters to continue.",
        actionLabel: "Clear Filters",
      };
    }

    return {
      tone: "neutral",
      title: "No enterprises yet",
      description: "Create your first enterprise account to begin decision calibration operations.",
      actionLabel: "Create Enterprise",
    };
  }, [activeTableError, hasActiveFilters]);

  const handleStateAction = () => {
    if (activeTableError) {
      refreshTables();
      return;
    }
    if (hasActiveFilters) {
      clearFilters();
      return;
    }
    openCreateEnterprise();
  };

  const renderNoDataState = (
    <div className={`enterprise-owners__stateCard enterprise-owners__stateCard--${tableState.tone}`}>
      <strong>{tableState.title}</strong>
      <p>{tableState.description}</p>
      <button type="button" className="enterprise-owners__btn enterprise-owners__btn--ghost" onClick={handleStateAction}>
        {tableState.actionLabel}
      </button>
    </div>
  );

  const renderDetailsLoading = () => (
    <div className="enterprise-owners__detailSkeleton" aria-live="polite">
      <div className="enterprise-owners__detailSkeletonRow" />
      <div className="enterprise-owners__detailSkeletonRow" />
      <div className="enterprise-owners__detailSkeletonRow" />
      <div className="enterprise-owners__detailSkeletonRow is-short" />
    </div>
  );

  const renderOverviewTab = () => (
    <div className="enterprise-owners__detailGrid">
      <div className="enterprise-owners__detailCard enterprise-owners__detailCard--highlight">
        <h5>Seat Usage Snapshot</h5>
        <div className="enterprise-owners__statsGrid">
          <div>
            <span>Active Participants</span>
            <strong>{seatUsage?.activeUsers || 0}</strong>
          </div>
          <div>
            <span>Invitations Pending</span>
            <strong>{seatUsage?.invitedUsers || 0}</strong>
          </div>
          <div>
            <span>Enterprise Lead Count</span>
            <strong>{seatUsage?.ownerCount || 0}</strong>
          </div>
          <div>
            <span>Allocated Seats</span>
            <strong>{detailsEnterprise?.plan?.seatCount || 0}</strong>
          </div>
        </div>
      </div>

      <div className="enterprise-owners__detailCard">
        <h5>Enterprise Logo</h5>
        <div className="enterprise-owners__sectionHint">
          Optional. Upload PNG, JPG, WEBP, or SVG up to {LOGO_MAX_MB}MB.
        </div>
        <div className="enterprise-owners__logoLayout">
          <div className="enterprise-owners__logoCard">
            <div className="enterprise-owners__logoCardTitle">Current Logo</div>
            <div className="enterprise-owners__logoVisual">
              {detailsEnterprise?.logoUrl && !logoImageError ? (
                <img
                  src={`${detailsEnterprise.logoUrl}${
                    detailsEnterprise?.logoUpdatedAt
                      ? `?v=${new Date(detailsEnterprise.logoUpdatedAt).getTime()}`
                      : ""
                  }`}
                  alt={`${detailsEnterprise?.name || "Enterprise"} logo`}
                  className="enterprise-owners__logoPreview"
                  onError={() => setLogoImageError(true)}
                />
              ) : (
                <div className="enterprise-owners__logoFallback">
                  {getInitials(detailsEnterprise?.name, detailsEnterprise?.companyName)}
                </div>
              )}
            </div>
          </div>

          <div className="enterprise-owners__logoCard">
            <div className="enterprise-owners__logoCardTitle">
              New Preview
              {selectedLogoFile ? (
                <span className="enterprise-owners__logoStatusTag">Unsaved</span>
              ) : null}
            </div>
            <div className="enterprise-owners__logoVisual">
              {selectedLogoPreviewUrl ? (
                <img
                  src={selectedLogoPreviewUrl}
                  alt="New logo preview"
                  className="enterprise-owners__logoPreview"
                />
              ) : (
                <div className="enterprise-owners__logoPlaceholderText">
                  Choose an image to preview before saving.
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="enterprise-owners__logoActions">
          <label className="enterprise-owners__uiBtn enterprise-owners__uiBtn--accent enterprise-owners__uiBtn--compact mb-0">
            {selectedLogoFile ? "Change Image" : detailsEnterprise?.logoUrl ? "Replace Logo" : "Upload Logo"}
            <input
              ref={logoInputRef}
              type="file"
              accept={LOGO_ALLOWED_EXTENSIONS.join(",")}
              onChange={handleEnterpriseLogoFileSelect}
              hidden
              disabled={logoBusy}
            />
          </label>
          <button
            type="button"
            className="enterprise-owners__uiBtn enterprise-owners__uiBtn--primary enterprise-owners__uiBtn--compact"
            onClick={saveEnterpriseLogoByAdmin}
            disabled={logoBusy || !selectedLogoFile}
          >
            {logoBusy ? "Saving..." : "Save Logo"}
          </button>
          <button
            type="button"
            className="enterprise-owners__uiBtn enterprise-owners__uiBtn--neutral enterprise-owners__uiBtn--compact"
            onClick={clearSelectedEnterpriseLogo}
            disabled={logoBusy || !selectedLogoFile}
          >
            Cancel
          </button>
          <button
            type="button"
            className="enterprise-owners__uiBtn enterprise-owners__uiBtn--neutral enterprise-owners__uiBtn--compact"
            onClick={removeEnterpriseLogoByAdmin}
            disabled={logoBusy || !detailsEnterprise?.logoUrl || Boolean(selectedLogoFile)}
          >
            Remove Logo
          </button>
        </div>
        {selectedLogoFile ? (
          <div className="enterprise-owners__fieldHint">
            Preview ready. Click <strong>Save Logo</strong> to apply changes.
          </div>
        ) : null}
      </div>

      <div className="enterprise-owners__detailCard">
        <h5>Enterprise Configuration</h5>
        <div className="form-row">
          <div className="form-group col-md-6">
            <label>Enterprise Name</label>
            <input
              className="form-control"
              name="enterpriseName"
              value={detailsForm.enterpriseName}
              onChange={handleDetailsFormChange}
            />
          </div>
          <div className="form-group col-md-6">
            <label>Company Name (Optional)</label>
            <input
              className="form-control"
              name="companyName"
              value={detailsForm.companyName}
              onChange={handleDetailsFormChange}
            />
          </div>
              <div className="form-group col-md-6">
                <label>
                  Company Domain <span className="enterprise-owners__requiredMark">*</span>
                </label>
                <input
                  className={`form-control ${detailsFormErrors.companyDomain ? "is-invalid" : ""}`}
                  name="companyDomain"
                  value={detailsForm.companyDomain}
                  onChange={handleDetailsFormChange}
                  aria-invalid={Boolean(detailsFormErrors.companyDomain)}
                />
                {detailsFormErrors.companyDomain ? (
                  <small className="enterprise-owners__fieldError">{detailsFormErrors.companyDomain}</small>
                ) : null}
              </div>
          <div className="form-group col-md-6">
            <label>Enterprise Plan</label>
            <input
              className="form-control"
              name="planType"
              value={detailsForm.planType}
              onChange={handleDetailsFormChange}
            />
          </div>
          <div className="form-group col-md-4">
            <label>Allocated Seats</label>
            <input
              type="number"
              className="form-control"
              name="seatCount"
              min={1}
              value={detailsForm.seatCount}
              onChange={handleDetailsFormChange}
            />
            <small className="enterprise-owners__fieldHint">
              Seats define how many participants can join calibration sessions.
            </small>
          </div>
          <div className="form-group col-md-4">
            <label>Calibration Start Date</label>
            <input
              type="date"
              className="form-control"
              name="startDate"
              value={detailsForm.startDate}
              onChange={handleDetailsFormChange}
            />
          </div>
          <div className="form-group col-md-4">
            <label>Calibration End Date</label>
            <input
              type="date"
              className="form-control"
              name="endDate"
              value={detailsForm.endDate}
              onChange={handleDetailsFormChange}
            />
          </div>
          <div className="form-group col-md-4">
            <label>Grace Period After End Date (Days)</label>
            <input
              type="number"
              className="form-control"
              name="gracePeriod"
              min={0}
              value={detailsForm.gracePeriod}
              onChange={handleDetailsFormChange}
            />
            <small className="enterprise-owners__fieldHint">
              After the end date, users keep access for this many extra days before full expiry.
              Use 0 for no grace period.
            </small>
          </div>
          {SHOW_ACCOUNT_MANAGER_FIELD ? (
            <div className="form-group col-md-4">
              <label>Account Manager</label>
              <input
                className="form-control"
                name="accountManager"
                value={detailsForm.accountManager}
                onChange={handleDetailsFormChange}
              />
            </div>
          ) : null}
          {SHOW_PRIORITY_SUPPORT_FIELD ? (
            <div className="form-group col-md-4 d-flex align-items-end">
              <label className="mb-0">
                <input
                  type="checkbox"
                  className="mr-2"
                  name="prioritySupport"
                  checked={detailsForm.prioritySupport}
                  onChange={handleDetailsFormChange}
                />
                Priority Support
              </label>
            </div>
          ) : null}
        </div>
      </div>

      <div className="enterprise-owners__detailCard">
        <h5>Enterprise Lead Profile</h5>
        <div className="form-row">
          <div className="form-group col-md-4">
            <label>Enterprise Lead Name</label>
            <input
              className="form-control"
              name="ownerName"
              value={detailsForm.ownerName}
              onChange={handleDetailsFormChange}
            />
          </div>
          <div className="form-group col-md-4">
            <label>Enterprise Lead Email</label>
            <input
              className={`form-control ${detailsFormErrors.ownerEmail ? "is-invalid" : ""}`}
              name="ownerEmail"
              value={detailsForm.ownerEmail}
              disabled
              readOnly
              aria-describedby="enterprise-owner-email-help"
              aria-invalid={Boolean(detailsFormErrors.ownerEmail)}
            />
            <small id="enterprise-owner-email-help" className="form-text text-muted">
              To change the Enterprise Lead email, use Transfer Enterprise Lead below.
            </small>
            {detailsFormErrors.ownerEmail ? (
              <small className="enterprise-owners__fieldError">{detailsFormErrors.ownerEmail}</small>
            ) : null}
          </div>
          <div className="form-group col-md-4">
            <label>Enterprise Lead Phone</label>
            <input
              className="form-control"
              name="ownerPhone"
              value={detailsForm.ownerPhone}
              onChange={handleDetailsFormChange}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group col-md-6">
            <label>
              Organization Name <span className="enterprise-owners__requiredMark">*</span>
            </label>
            <input
              className={`form-control ${detailsFormErrors.ownerOrganizationName ? "is-invalid" : ""}`}
              name="ownerOrganizationName"
              value={detailsForm.ownerOrganizationName}
              onChange={handleDetailsFormChange}
              aria-invalid={Boolean(detailsFormErrors.ownerOrganizationName)}
            />
            {detailsFormErrors.ownerOrganizationName ? (
              <small className="enterprise-owners__fieldError">{detailsFormErrors.ownerOrganizationName}</small>
            ) : null}
          </div>
          <div className="form-group col-md-6">
            <label>Industry (Optional)</label>
            <select
              className="form-control"
              name="ownerIndustry"
              value={detailsForm.ownerIndustry}
              onChange={handleDetailsFormChange}
            >
              <option value="">Select industry</option>
              {OWNER_INDUSTRY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group col-md-6">
            <label>Organization Size (Optional)</label>
            <select
              className="form-control"
              name="ownerOrganizationSize"
              value={detailsForm.ownerOrganizationSize}
              onChange={handleDetailsFormChange}
            >
              <option value="">Select organization size</option>
              {OWNER_ORGANIZATION_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group col-md-6">
            <label>Geographic Scope (Optional)</label>
            <select
              className="form-control"
              name="ownerGeographicScope"
              value={detailsForm.ownerGeographicScope}
              onChange={handleDetailsFormChange}
            >
              <option value="">Select geographic scope</option>
              {OWNER_GEOGRAPHIC_SCOPE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        <h6 className="mt-2">Transfer Enterprise Lead</h6>
        <div className="form-row">
          <div className="form-group col-md-5">
            <input
              className="form-control"
              placeholder="New Enterprise Lead full name"
              value={transferOwnership.ownerName}
              disabled={transferOwnershipSubmitting}
              onChange={(event) => {
                const value = event.target.value;
                setTransferOwnership((prev) => ({
                  ...prev,
                  ownerName: value,
                }));
                setTransferOwnershipError("");
              }}
            />
          </div>
          <div className="form-group col-md-5">
            <input
              className="form-control"
              placeholder="new-lead@company.com"
              value={transferOwnership.ownerEmail}
              disabled={transferOwnershipSubmitting}
              aria-describedby="transfer-enterprise-lead-error"
              aria-invalid={Boolean(transferOwnershipError)}
              onChange={(event) => {
                const value = event.target.value;
                setTransferOwnership((prev) => ({
                  ...prev,
                  membershipId: "",
                  ownerEmail: value,
                }));
                setTransferOwnershipError("");
              }}
            />
            {transferOwnershipError ? (
              <small id="transfer-enterprise-lead-error" className="enterprise-owners__fieldError">
                {transferOwnershipError}
              </small>
            ) : null}
          </div>
          <div className="form-group col-md-2">
            <button
              type="button"
              className="enterprise-owners__uiBtn enterprise-owners__uiBtn--accent enterprise-owners__uiBtn--block"
              onClick={submitOwnershipTransfer}
              disabled={transferOwnershipSubmitting}
            >
              {transferOwnershipSubmitting ? "Transferring..." : "Transfer Lead"}
            </button>
          </div>
        </div>
      </div>

      <div className="enterprise-owners__detailCard">
        <h5>Enterprise Access Rules</h5>
        <div className="enterprise-owners__sectionHint">
          Define domain, approval, and identity controls for enterprise access.
        </div>
        <div className="form-row">
          <div className="form-group col-md-6">
            <label>Allowed Email Domains (comma separated)</label>
            <input
              className={`form-control ${detailsFormErrors.allowedEmailDomains ? "is-invalid" : ""}`}
              name="allowedEmailDomains"
              value={detailsForm.allowedEmailDomains}
              onChange={handleDetailsFormChange}
              placeholder="company.com, subsidiary.com"
              aria-invalid={Boolean(detailsFormErrors.allowedEmailDomains)}
            />
            {detailsFormErrors.allowedEmailDomains ? (
              <small className="enterprise-owners__fieldError">
                {detailsFormErrors.allowedEmailDomains}
              </small>
            ) : null}
          </div>
          <div className="form-group col-md-6 d-flex align-items-end">
            <div className="w-100">
              <label className="d-block enterprise-owners__checkRow">
                <input
                  type="checkbox"
                  className="mr-2"
                  name="domainRestrictionEnabled"
                  checked={detailsForm.domainRestrictionEnabled}
                  onChange={handleDetailsFormChange}
                />
                Restrict to Allowed Domains
              </label>
              <label className="d-block enterprise-owners__checkRow">
                <input
                  type="checkbox"
                  className="mr-2"
                  name="ssoRequired"
                  checked={detailsForm.ssoRequired}
                  onChange={handleDetailsFormChange}
                />
                Require SSO
              </label>
              <label className="d-block mb-0 enterprise-owners__checkRow">
                <input
                  type="checkbox"
                  className="mr-2"
                  name="manualApprovalRequired"
                  checked={detailsForm.manualApprovalRequired}
                  onChange={handleDetailsFormChange}
                />
                Require Manual Approval
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="enterprise-owners__detailCard">
        <h5>Lifecycle and Enterprise Lead Permissions</h5>
        <div className="enterprise-owners__subGrid">
          <div className="enterprise-owners__subCard">
            <h6>Lifecycle Controls</h6>
            <label className="d-block enterprise-owners__checkRow">
              <input
                type="checkbox"
                className="mr-2"
                name="autoExpiryEnabled"
                checked={detailsForm.autoExpiryEnabled}
                onChange={handleDetailsFormChange}
              />
              Enable Auto Expiry
            </label>
            <label className="d-block enterprise-owners__checkRow">
              <input
                type="checkbox"
                className="mr-2"
                name="reminderNotificationsEnabled"
                checked={detailsForm.reminderNotificationsEnabled}
                onChange={handleDetailsFormChange}
              />
              Lifecycle Reminder Notifications
            </label>
            <label className="d-block enterprise-owners__checkRow">
              <input
                type="checkbox"
                className="mr-2"
                name="seatThresholdAlertsEnabled"
                checked={detailsForm.seatThresholdAlertsEnabled}
                onChange={handleDetailsFormChange}
              />
              Enable Seat Threshold Alerts
            </label>
            <label className="d-block enterprise-owners__checkRow">
              <input
                type="checkbox"
                className="mr-2"
                name="renewalRemindersEnabled"
                checked={detailsForm.renewalRemindersEnabled}
                onChange={handleDetailsFormChange}
              />
              Enable Renewal Reminders
            </label>
            <label className="d-block enterprise-owners__checkRow">
              <input
                type="checkbox"
                className="mr-2"
                name="upgradeAvailable"
                checked={detailsForm.upgradeAvailable}
                onChange={handleDetailsFormChange}
              />
              Allow Upgrade Requests
            </label>
            <label className="d-block mb-0 enterprise-owners__checkRow">
              <input
                type="checkbox"
                className="mr-2"
                name="downgradeAvailable"
                checked={detailsForm.downgradeAvailable}
                onChange={handleDetailsFormChange}
              />
              Allow Downgrade Requests
            </label>
          </div>
          <div className="enterprise-owners__subCard">
            <h6>Enterprise Lead Permissions</h6>
            <label className="d-block enterprise-owners__checkRow">
              <input
                type="checkbox"
                className="mr-2"
                checked={detailsForm.ownerPermissions?.canAddUsers}
                onChange={(event) =>
                  handleOwnerPermissionChange("canAddUsers", event.target.checked)
                }
              />
              Enterprise Lead can invite participants
            </label>
            <label className="d-block enterprise-owners__checkRow">
              <input
                type="checkbox"
                className="mr-2"
                checked={detailsForm.ownerPermissions?.canDeactivateUsers}
                onChange={(event) =>
                  handleOwnerPermissionChange(
                    "canDeactivateUsers",
                    event.target.checked
                  )
                }
              />
              Enterprise Lead can deactivate participants
            </label>
            <label className="d-block enterprise-owners__checkRow">
              <input
                type="checkbox"
                className="mr-2"
                checked={detailsForm.ownerPermissions?.canResendInvites}
                onChange={(event) =>
                  handleOwnerPermissionChange(
                    "canResendInvites",
                    event.target.checked
                  )
                }
              />
              Enterprise Lead can resend invitations
            </label>
            <label className="d-block mb-0 enterprise-owners__checkRow">
              <input
                type="checkbox"
                className="mr-2"
                checked={detailsForm.ownerPermissions?.canViewUserActivity}
                onChange={(event) =>
                  handleOwnerPermissionChange(
                    "canViewUserActivity",
                    event.target.checked
                  )
                }
              />
              Enterprise Lead can view participant activity
            </label>
          </div>
        </div>
      </div>

      {SHOW_NOTES_SECTION ? (
      <div className="enterprise-owners__detailCard">
        <h5>{SHOW_ADVANCED_ENTERPRISE_CONFIG ? "Notes + Advanced Config" : "Notes"}</h5>
        <div className="form-row">
          <div className="form-group col-md-6">
            <label>Internal Notes</label>
            <textarea
              className="form-control"
              rows={3}
              name="internalNotes"
              value={detailsForm.internalNotes}
              onChange={handleDetailsFormChange}
            />
          </div>
          <div className="form-group col-md-6">
            <label>Sales Notes</label>
            <textarea
              className="form-control"
              rows={3}
              name="salesNotes"
              value={detailsForm.salesNotes}
              onChange={handleDetailsFormChange}
            />
          </div>
          <div className="form-group col-md-6">
            <label>Support Notes</label>
            <textarea
              className="form-control"
              rows={3}
              name="supportNotes"
              value={detailsForm.supportNotes}
              onChange={handleDetailsFormChange}
            />
          </div>
          <div className="form-group col-md-6">
            <label>Renewal Notes</label>
            <textarea
              className="form-control"
              rows={3}
              name="renewalNotes"
              value={detailsForm.renewalNotes}
              onChange={handleDetailsFormChange}
            />
          </div>
          {SHOW_ADVANCED_ENTERPRISE_CONFIG ? (
            <>
          <div className="form-group col-md-6">
            <div className="enterprise-owners__configBlock">
              <div className="enterprise-owners__configHeader">
                <label className="mb-0">Feature Flags</label>
                <span>Turn enterprise features on or off</span>
              </div>
              <div className="enterprise-owners__kvRows">
              {(detailsForm.featureFlagRows || []).map((item, index) => (
                <div key={`details-flag-${index}`} className="enterprise-owners__kvRow">
                  <input
                    className="form-control enterprise-owners__kvInput"
                    placeholder="Feature name (example: challenge_access)"
                    value={item.key}
                    onChange={(event) =>
                      updateFeatureFlagRow("change", index, "key", event.target.value)
                    }
                  />
                  <select
                    className="form-control enterprise-owners__kvInput"
                    value={item.enabled ? "enabled" : "disabled"}
                    onChange={(event) =>
                      updateFeatureFlagRow(
                        "change",
                        index,
                        "enabled",
                        event.target.value === "enabled"
                      )
                    }
                  >
                    <option value="enabled">Enabled</option>
                    <option value="disabled">Disabled</option>
                  </select>
                  <button
                    type="button"
                    className="enterprise-owners__uiBtn enterprise-owners__uiBtn--neutral enterprise-owners__kvRemove"
                    onClick={() => updateFeatureFlagRow("remove", index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="enterprise-owners__uiBtn enterprise-owners__uiBtn--accent"
                onClick={() => updateFeatureFlagRow("add")}
              >
                Add Feature Flag
              </button>
              </div>
            </div>
          </div>
          <div className="form-group col-md-6">
            <div className="enterprise-owners__configBlock">
              <div className="enterprise-owners__configHeader">
                <label className="mb-0">Usage Limits</label>
                <span>Set enterprise usage caps</span>
              </div>
              <div className="enterprise-owners__kvRows">
              {(detailsForm.usageLimitRows || []).map((item, index) => (
                <div key={`details-limit-${index}`} className="enterprise-owners__kvRow">
                  <input
                    className="form-control enterprise-owners__kvInput"
                    placeholder="Limit name (example: monthly_challenges)"
                    value={item.key}
                    onChange={(event) =>
                      updateUsageLimitRow("change", index, "key", event.target.value)
                    }
                  />
                  <input
                    className="form-control enterprise-owners__kvInput"
                    placeholder="Value (number or text)"
                    value={item.value}
                    onChange={(event) =>
                      updateUsageLimitRow("change", index, "value", event.target.value)
                    }
                  />
                  <button
                    type="button"
                    className="enterprise-owners__uiBtn enterprise-owners__uiBtn--neutral enterprise-owners__kvRemove"
                    onClick={() => updateUsageLimitRow("remove", index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="enterprise-owners__uiBtn enterprise-owners__uiBtn--accent"
                onClick={() => updateUsageLimitRow("add")}
              >
                Add Usage Limit
              </button>
              </div>
            </div>
          </div>
            </>
          ) : null}
        </div>
      </div>
      ) : null}

      <div className="enterprise-owners__detailCard">
        <h5>Lifecycle Controls</h5>
        <div className="enterprise-owners__actions">
          <button
            type="button"
            className="enterprise-owners__actionBtn"
            onClick={() => runEnterpriseAction("save_as_draft")}
          >
            Save as Setup in Progress
          </button>
          <button
            type="button"
            className="enterprise-owners__actionBtn"
            onClick={() =>
              runEnterpriseAction("save_as_scheduled", {
                startDate: detailsForm.startDate || null,
              })
            }
          >
            Save as Scheduled
          </button>
          <button
            type="button"
            className="enterprise-owners__actionBtn enterprise-owners__actionBtn--success"
            onClick={() => runEnterpriseAction("activate")}
          >
            Activate
          </button>
          <button
            type="button"
            className="enterprise-owners__actionBtn enterprise-owners__actionBtn--danger"
            onClick={() => runEnterpriseAction("deactivate")}
          >
            Deactivate
          </button>
          <button
            type="button"
            className="enterprise-owners__actionBtn enterprise-owners__actionBtn--danger"
            onClick={() => runEnterpriseAction("suspend")}
          >
            Suspend
          </button>
          <button
            type="button"
            className="enterprise-owners__actionBtn enterprise-owners__actionBtn--danger"
            onClick={() => runEnterpriseAction("mark_expired")}
          >
            Mark as Closed
          </button>
          <button
            type="button"
            className="enterprise-owners__actionBtn enterprise-owners__actionBtn--success"
            onClick={() => runEnterpriseAction("renew", { endDate: detailsForm.endDate || null })}
          >
            Renew
          </button>
          <button
            type="button"
            className="enterprise-owners__actionBtn"
            onClick={() =>
              runEnterpriseAction("change_plan", {
                planType: detailsForm.planType,
              })
            }
          >
            Change Plan
          </button>
          <button
            type="button"
            className="enterprise-owners__actionBtn"
            onClick={() => runEnterpriseAction("increase_seats", { delta: 1 })}
          >
            Increase Seat Allocation
          </button>
          <button
            type="button"
            className="enterprise-owners__actionBtn"
            onClick={() => runEnterpriseAction("decrease_seats", { delta: 1 })}
          >
            Decrease Seat Allocation
          </button>
        </div>
      </div>

    </div>
  );

  const renderUsersTab = () => (
    <div className="enterprise-owners__detailGrid">
      <div className="enterprise-owners__detailCard">
        <h5>Invite Enterprise Participant or Lead</h5>
        <div className="form-row">
          <div className="form-group col-md-2">
            <label>Role</label>
            <select
              className="form-control"
              value={inviteForm.role}
              onChange={(event) => {
                const value = event.target.value;
                setInviteForm((prev) => normalizeInviteFormForRole(prev, value));
              }}
            >
              <option value="enterprise_user">Participant</option>
              <option value="enterprise_owner">Enterprise Lead</option>
            </select>
          </div>
          <div className="form-group col-md-3">
            <label>Name</label>
            <input
              className="form-control"
              value={inviteForm.name}
              onChange={(event) => {
                const value = event.target.value;
                setInviteForm((prev) => ({ ...prev, name: value }));
              }}
              placeholder="Full name"
            />
          </div>
          <div className="form-group col-md-3">
            <label>Email</label>
            <input
              className="form-control"
              value={inviteForm.email}
              onChange={(event) => {
                const value = event.target.value;
                setInviteForm((prev) => ({ ...prev, email: value }));
              }}
              placeholder="user@company.com"
            />
          </div>
          <div className="form-group col-md-4 d-flex align-items-end">
            <button
              type="button"
              className="enterprise-owners__uiBtn enterprise-owners__uiBtn--accent enterprise-owners__uiBtn--block"
              onClick={sendInvite}
            >
              Send Invitation
            </button>
          </div>
        </div>
        <small className="form-text text-muted">
          Participants complete department, leadership level, and relationship details in My Profile &gt; Settings.
        </small>
        {inviteForm.role === "enterprise_owner" ? (
          <div className="form-row">
            <div className="form-group col-md-3">
              <label className="d-block">Owner Invite Mode</label>
              <select
                className="form-control"
                value={inviteForm.defaultRoleMode || "owner_and_participant"}
                onChange={(event) =>
                  setInviteForm((prev) => ({
                    ...prev,
                    defaultRoleMode: event.target.value,
                  }))
                }
              >
                <option value="owner_only">Owner only</option>
                <option value="owner_and_participant">Owner + Participant</option>
                {inviteForm.allowParticipantOnly ? (
                  <option value="participant_only">Participant only</option>
                ) : null}
              </select>
            </div>
            <div className="form-group col-md-3 d-flex align-items-end">
              <label className="d-block mb-0">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={Boolean(inviteForm.allowParticipantRole)}
                  onChange={(event) =>
                    setInviteForm((prev) => ({
                      ...prev,
                      allowParticipantRole: event.target.checked,
                      defaultRoleMode: event.target.checked
                        ? prev.defaultRoleMode || "owner_and_participant"
                        : "owner_only",
                    }))
                  }
                />
                Allow Owner + Participant
              </label>
            </div>
            <div className="form-group col-md-3 d-flex align-items-end">
              <label className="d-block mb-0">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={Boolean(inviteForm.allowParticipantOnly)}
                  onChange={(event) =>
                    setInviteForm((prev) => ({
                      ...prev,
                      allowParticipantOnly: event.target.checked,
                    }))
                  }
                />
                Allow Participant only
              </label>
            </div>
          </div>
        ) : null}
      </div>

      <div className="enterprise-owners__detailCard">
        <h5>Enterprise Participants</h5>
        <div className="enterprise-owners__logsToolbar mb-3">
          <div className="enterprise-owners__sectionHint mb-0">
            Manage participant access and invitations from one table.
          </div>
          <button
            type="button"
            className="enterprise-owners__uiBtn enterprise-owners__uiBtn--accent"
            onClick={exportUsers}
          >
            Export Participant List CSV
          </button>
        </div>
        <div className="table-responsive">
          <table className="table table-sm table-borderless mb-0">
            <thead>
              <tr>
                <th>Email</th>
                <th>Participant Name</th>
                <th>Access Role</th>
                <th>Role Mode</th>
                <th>Lifecycle Status</th>
                <th>Invitation Sent</th>
                <th>Last Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {memberships.map((membership) => (
                <tr key={membership._id}>
                  <td>{membership.email}</td>
                  <td>{membership.name || "—"}</td>
                  <td>{ROLE_LABELS[String(membership.role || "").toLowerCase()] || "Participant"}</td>
                  <td>{toDisplayText(membership.roleMode || "participant_only")}</td>
                  <td>
                    <span className={`badge ${statusBadgeClass(membership.status)}`}>
                      {toStatusText(membership.status)}
                    </span>
                  </td>
                  <td>
                    {membership.invitedAt
                      ? moment(membership.invitedAt).format("MMM DD, YYYY")
                      : "—"}
                  </td>
                  <td>
                    {membership.lastLoginAt
                      ? moment(membership.lastLoginAt).format("MMM DD, YYYY")
                      : "—"}
                  </td>
                  <td>
                    <div className="enterprise-owners__actions">
                      <button
                        type="button"
                        className="enterprise-owners__actionBtn"
                        onClick={() => manageMembership(membership, "resend_invite")}
                      >
                        Resend Invite
                      </button>

                      {membership.status === "active" ? (
                        <button
                          type="button"
                          className="enterprise-owners__actionBtn enterprise-owners__actionBtn--danger"
                          onClick={() => manageMembership(membership, "deactivate_user")}
                        >
                          Deactivate
                        </button>
                      ) : null}

                      {["deactivated", "inactive", "invited"].includes(membership.status) ? (
                        <button
                          type="button"
                          className="enterprise-owners__actionBtn enterprise-owners__actionBtn--success"
                          onClick={() => manageMembership(membership, "reactivate_user")}
                        >
                          Reactivate
                        </button>
                      ) : null}

                      {membership.status === "invited" ? (
                        <button
                          type="button"
                          className="enterprise-owners__actionBtn"
                          onClick={() => manageMembership(membership, "cancel_invite")}
                        >
                          Cancel Invite
                        </button>
                      ) : null}

                      {membership.status !== "active" ? (
                        <button
                          type="button"
                          className="enterprise-owners__actionBtn enterprise-owners__actionBtn--danger"
                          onClick={() => manageMembership(membership, "remove_invite")}
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
              {!memberships.length ? (
                <tr>
                  <td colSpan={8} className="text-muted">
                    No participants added to this enterprise yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const loadVerificationDetail = async (verificationId) => {
    if (!verificationId) {
      setSelectedVerificationId("");
      setSelectedVerification(null);
      return;
    }

    setVerificationError("");
    try {
      const res = await ApiGet(`admin/verifications/${verificationId}`);
      const verification = res?.data?.payload?.verification || null;
      setSelectedVerification(verification);
      setVerificationDecisionReason(verification?.review?.rejectionReason || "");
      setSelectedVerificationId(verificationId);
    } catch (error) {
      setSelectedVerification(null);
      setVerificationError(error?.message || "Unable to load verification detail.");
    }
  };

  const handleVerificationDecision = async (decision) => {
    const verificationId = selectedVerification?._id;
    const status = String(selectedVerification?.status || "").toLowerCase();

    if (!verificationId) {
      toast.error("Select a verification record first.");
      return;
    }
    if (status !== "submitted") {
      toast.error("Only submitted verification can be approved or rejected.");
      return;
    }
    if (decision === "reject" && !verificationDecisionReason.trim()) {
      toast.error("Rejection reason is required.");
      return;
    }

    setVerificationActionLoading(true);
    setVerificationError("");
    try {
      if (decision === "approve") {
        await ApiPatch(`admin/verifications/${verificationId}/approve`, {});
        toast.success("Verification approved.");
      } else {
        await ApiPatch(`admin/verifications/${verificationId}/reject`, {
          rejectionReason: verificationDecisionReason.trim(),
        });
        toast.success("Verification rejected.");
      }

      await Promise.all([
        loadEnterpriseVerifications(),
        loadVerificationDetail(verificationId),
      ]);
    } catch (error) {
      const message = error?.message || "Unable to update verification status.";
      setVerificationError(message);
      toast.error(message);
    } finally {
      setVerificationActionLoading(false);
    }
  };

  const loadEnterpriseVerifications = async ({
    enterpriseId = detailsEnterpriseId,
    status = verificationStatusFilter,
  } = {}) => {
    if (!enterpriseId) return;

    setVerificationLoading(true);
    setVerificationError("");
    try {
      const query = new URLSearchParams({ enterpriseId: String(enterpriseId), limit: "300" });
      if (status && status !== "all") query.set("status", String(status));
      const res = await ApiGet(`admin/verifications?${query.toString()}`);
      const records = res?.data?.payload?.records || [];
      setVerificationRows(records);

      if (selectedVerificationId) {
        const matching = records.find((item) => String(item?._id || "") === String(selectedVerificationId));
        if (matching) {
          await loadVerificationDetail(selectedVerificationId);
        } else {
          setSelectedVerificationId("");
          setSelectedVerification(null);
        }
      }
    } catch (error) {
      setVerificationRows([]);
      setVerificationError(error?.message || "Unable to load enterprise verifications.");
    } finally {
      setVerificationLoading(false);
    }
  };

  useEffect(() => {
    if (!detailsOpen || detailTab !== "logs" || !detailsEnterpriseId) return;
    loadDetailLogs();
  }, [detailsOpen, detailTab, detailLogTab, detailLogPagination.page, detailLogPagination.limit, detailsEnterpriseId]);

  useEffect(() => {
    if (!detailsOpen || detailTab !== "verifications" || !detailsEnterpriseId) return;
    loadEnterpriseVerifications();
  }, [detailsOpen, detailTab, detailsEnterpriseId, verificationStatusFilter]);

  useEffect(() => {
    if (!detailsOpen || detailTab !== "performance" || !detailsEnterpriseId) return;
    loadPerformance();
  }, [detailsOpen, detailTab, detailsEnterpriseId, performanceWindow]);

  const handleLogTabChange = (tabKey) => {
    setDetailLogTab(tabKey);
    setDetailLogRows([]);
    setDetailLogsError("");
    setDetailLogPagination((prev) => ({ ...prev, page: 1, total: 0, totalPages: 1 }));
  };

  const goToDetailLogPage = (nextPage) => {
    setDetailLogPagination((prev) => ({
      ...prev,
      page: Math.min(Math.max(Number(nextPage || 1), 1), Math.max(prev.totalPages || 1, 1)),
    }));
  };

  const handleDetailLogPageSizeChange = (event) => {
    const nextLimit = Number(event.target.value || 10);
    setDetailLogPagination((prev) => ({
      ...prev,
      page: 1,
      limit: nextLimit,
      totalPages: 1,
      total: 0,
    }));
  };

  const renderVerificationsTab = () => {
    const summary = verificationRows.reduce(
      (acc, item) => {
        const status = String(item?.status || "not_started").toLowerCase();
        acc.total += 1;
        if (status === "submitted") acc.submitted += 1;
        if (status === "approved") acc.approved += 1;
        if (status === "rejected") acc.rejected += 1;
        if (status === "in_progress") acc.inProgress += 1;
        if (status === "skipped") acc.skipped += 1;
        return acc;
      },
      { total: 0, submitted: 0, approved: 0, rejected: 0, inProgress: 0, skipped: 0 }
    );

    const ownerRows = verificationRows.filter((row) =>
      isOwnerVerificationRow(row, detailsEnterprise)
    );
    const participantRows = verificationRows.filter(
      (row) => !isOwnerVerificationRow(row, detailsEnterprise)
    );
    const ownerVerification = ownerRows[0] || null;
    const ownerReviewAt = ownerVerification?.approvedAt || ownerVerification?.rejectedAt || null;
    const ownerDisplayName =
      detailsEnterprise?.ownerProfile?.name || ownerVerification?.user?.name || "Enterprise Lead";
    const ownerDisplayEmail =
      detailsEnterprise?.ownerProfile?.email || ownerVerification?.user?.email || "—";
    const ownerStatus = ownerVerification?.status || "not_started";
    const assets = selectedVerification?.assets || {};
    const reviewTime = selectedVerification?.approvedAt || selectedVerification?.rejectedAt || null;
    const canTakeVerificationDecision =
      String(selectedVerification?.status || "").toLowerCase() === "submitted";
    const selectedIsOwnerVerification = isOwnerVerificationRow(
      selectedVerification || {},
      detailsEnterprise
    );

    return (
      <div className="enterprise-owners__verificationCanvas">
        <div className="enterprise-owners__verificationKpis">
          <div className="enterprise-owners__verificationKpi"><span>Total Records</span><strong>{summary.total}</strong></div>
          <div className="enterprise-owners__verificationKpi"><span>Pending Review</span><strong>{summary.submitted}</strong></div>
          <div className="enterprise-owners__verificationKpi"><span>Approved</span><strong>{summary.approved}</strong></div>
          <div className="enterprise-owners__verificationKpi"><span>Rejected</span><strong>{summary.rejected}</strong></div>
          <div className="enterprise-owners__verificationKpi"><span>In Progress</span><strong>{summary.inProgress}</strong></div>
          <div className="enterprise-owners__verificationKpi"><span>Skipped</span><strong>{summary.skipped}</strong></div>
        </div>

        <div className="enterprise-owners__verificationSplit">
          <section className="enterprise-owners__verificationPanel">
            <div className="enterprise-owners__verificationToolbar">
              <div className="enterprise-owners__sectionHint mb-0">
                Review enterprise lead verification and participant submissions in one place.
              </div>
              <div className="enterprise-owners__verificationToolbarActions">
                <select
                  className="form-control enterprise-owners__verificationSelect"
                  value={verificationStatusFilter}
                  onChange={(event) => setVerificationStatusFilter(event.target.value)}
                >
                  {VERIFICATION_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="enterprise-owners__uiBtn enterprise-owners__uiBtn--accent"
                  onClick={() => loadEnterpriseVerifications()}
                  disabled={verificationLoading}
                >
                  {verificationLoading ? "Refreshing..." : "Refresh"}
                </button>
              </div>
            </div>

            <div className="enterprise-owners__ownerVerificationCard">
              <div className="enterprise-owners__ownerVerificationHeader">
                <div>
                  <h6 className="mb-1">Enterprise Lead Verification</h6>
                  <p className="mb-0">Admin can review this separately from participant records.</p>
                </div>
                <span className={`badge ${statusBadgeClass(ownerStatus)}`}>
                  {toStatusText(ownerStatus)}
                </span>
              </div>
              <div className="enterprise-owners__ownerVerificationIdentity">
                <strong>{ownerDisplayName}</strong>
                <span>{ownerDisplayEmail}</span>
              </div>
              <div className="enterprise-owners__ownerVerificationMeta">
                <div>
                  <span>Submitted</span>
                  <strong>{formatDate(ownerVerification?.submittedAt, "MMM DD, YYYY HH:mm")}</strong>
                </div>
                <div>
                  <span>Reviewed</span>
                  <strong>{formatDate(ownerReviewAt, "MMM DD, YYYY HH:mm")}</strong>
                </div>
                <div>
                  <span>Skipped</span>
                  <strong>{formatDate(ownerVerification?.skippedAt, "MMM DD, YYYY HH:mm")}</strong>
                </div>
              </div>
              {ownerVerification ? (
                <button
                  type="button"
                  className="enterprise-owners__uiBtn enterprise-owners__uiBtn--primary enterprise-owners__uiBtn--compact"
                  onClick={() => loadVerificationDetail(ownerVerification?._id)}
                >
                  View Lead Verification Detail
                </button>
              ) : (
                <small className="text-muted d-block">
                  No verification record yet for the enterprise lead.
                </small>
              )}
            </div>

            <div className="enterprise-owners__verificationSubheader">
              <h6 className="mb-0">Participant Verifications</h6>
              <small>{participantRows.length} records</small>
            </div>
            <div className="table-responsive enterprise-owners__verificationTableWrap">
              <table className="table table-sm table-borderless mb-0 enterprise-owners__verificationTable">
                <thead>
                  <tr>
                    <th>Participant</th>
                    <th>Status</th>
                    <th>Avg Cycle Participants</th>
                    <th>Submitted</th>
                    <th>Reviewed</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {verificationLoading ? (
                    <tr>
                      <td colSpan={6} className="text-muted">Loading verification records...</td>
                    </tr>
                  ) : null}
                  {!verificationLoading && verificationError ? (
                    <tr>
                      <td colSpan={6} className="text-danger">{verificationError}</td>
                    </tr>
                  ) : null}
                  {!verificationLoading && !verificationError && participantRows.map((row) => {
                    const reviewedAt = row?.approvedAt || row?.rejectedAt || null;
                    const active = String(selectedVerificationId || "") === String(row?._id || "");
                    return (
                      <tr
                        key={row?._id}
                        className={active ? "enterprise-owners__comparisonRowActive" : ""}
                        onClick={() => loadVerificationDetail(row?._id)}
                      >
                        <td>
                          <div>{row?.user?.name || "Unknown"}</div>
                          <small className="text-muted">{row?.user?.email || "—"}</small>
                        </td>
                        <td>
                          <span className={`badge ${statusBadgeClass(row?.status)}`}>
                            {toStatusText(row?.status)}
                          </span>
                        </td>
                        <td>{Number.isFinite(Number(row?.avgCycleParticipants)) ? Number(row.avgCycleParticipants).toFixed(2) : "—"}</td>
                        <td>{formatDate(row?.submittedAt, "MMM DD, YYYY HH:mm")}</td>
                        <td>{formatDate(reviewedAt, "MMM DD, YYYY HH:mm")}</td>
                        <td>
                          <button
                            type="button"
                            className="enterprise-owners__actionBtn enterprise-owners__actionBtn--primary"
                            onClick={() => loadVerificationDetail(row?._id)}
                          >
                            Open
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {!verificationLoading && !verificationError && !participantRows.length ? (
                    <tr>
                      <td colSpan={6} className="text-muted">No participant verification records for this enterprise.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>

          <section className="enterprise-owners__verificationPanel enterprise-owners__verificationPanel--detail">
            {selectedVerification ? (
              <>
                <div className="enterprise-owners__verificationDetailHeader">
                  <div>
                    <h5 className="mb-1">Verification Detail</h5>
                    <small className="text-muted">
                      {selectedIsOwnerVerification ? "Enterprise Lead submission" : "Participant submission"}
                    </small>
                  </div>
                  <span className={`badge ${statusBadgeClass(selectedVerification?.status)}`}>
                    {toStatusText(selectedVerification?.status)}
                  </span>
                </div>
                <div className="enterprise-owners__verificationMetaGrid">
                  <div><span>Submitted At</span><strong>{formatDate(selectedVerification?.submittedAt, "MMM DD, YYYY HH:mm")}</strong></div>
                  <div><span>Reviewed At</span><strong>{formatDate(reviewTime, "MMM DD, YYYY HH:mm")}</strong></div>
                  <div><span>Skipped At</span><strong>{formatDate(selectedVerification?.skippedAt, "MMM DD, YYYY HH:mm")}</strong></div>
                  <div><span>Verification Id</span><strong>{selectedVerification?._id || "—"}</strong></div>
                  <div>
                    <span>Linked Profile</span>
                    <strong>
                      {selectedVerification?.linkedProfile ? (
                        isLikelyUrl(selectedVerification.linkedProfile) ? (
                          <a href={selectedVerification.linkedProfile} target="_blank" rel="noreferrer">
                            {selectedVerification.linkedProfile}
                          </a>
                        ) : (
                          selectedVerification.linkedProfile
                        )
                      ) : (
                        "—"
                      )}
                    </strong>
                  </div>
                  <div><span>Country</span><strong>{selectedVerification?.country || "—"}</strong></div>
                </div>

                {selectedVerification?.review?.rejectionReason ? (
                  <div className="alert alert-light-danger mt-3 mb-3">
                    <strong>Rejection reason:</strong> {selectedVerification.review.rejectionReason}
                  </div>
                ) : null}

                <div className="enterprise-owners__verificationAssetGrid">
                  {[
                    { key: "livePhoto", label: "Live Photo" },
                    { key: "documentFront", label: "Document Front" },
                    { key: "documentBack", label: "Document Back" },
                  ].map((item) => {
                    const url = assets?.[item.key]?.url || "";
                    return (
                      <div className="enterprise-owners__verificationAssetCard" key={item.key}>
                        <span>{item.label}</span>
                        {url ? (
                          <>
                            <button
                              type="button"
                              className="enterprise-owners__verificationThumbBtn"
                              onClick={() => setVerificationPreview({ url, label: item.label })}
                            >
                              <img
                                src={url}
                                alt={item.label}
                                className="enterprise-owners__verificationThumb"
                              />
                            </button>
                            <small className="text-muted d-block mt-2">
                              Uploaded: {formatDate(assets?.[item.key]?.uploadedAt, "MMM DD, YYYY HH:mm")}
                            </small>
                          </>
                        ) : (
                          <strong className="text-muted">Not uploaded</strong>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="enterprise-owners__verificationDecision">
                  <div className="form-group mt-0 mb-2">
                    <label className="font-weight-bold">Rejection Reason</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="Add reason when rejecting this verification"
                      value={verificationDecisionReason}
                      onChange={(event) => setVerificationDecisionReason(event.target.value)}
                      disabled={verificationActionLoading || !canTakeVerificationDecision}
                    />
                    {!canTakeVerificationDecision ? (
                      <small className="text-muted">
                        Decision controls are enabled only when verification status is Submitted.
                      </small>
                    ) : null}
                  </div>

                  <div className="enterprise-owners__actions mt-2">
                    <button
                      type="button"
                      className="enterprise-owners__actionBtn enterprise-owners__actionBtn--success"
                      onClick={() => handleVerificationDecision("approve")}
                      disabled={verificationActionLoading || !canTakeVerificationDecision}
                    >
                      {verificationActionLoading ? "Please wait..." : "Approve Verification"}
                    </button>
                    <button
                      type="button"
                      className="enterprise-owners__actionBtn enterprise-owners__actionBtn--danger"
                      onClick={() => handleVerificationDecision("reject")}
                      disabled={verificationActionLoading || !canTakeVerificationDecision}
                    >
                      {verificationActionLoading ? "Please wait..." : "Reject Verification"}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="enterprise-owners__verificationEmpty">
                Select a participant from the left table to view complete verification details.
              </div>
            )}
          </section>
        </div>
      </div>
    );
  };

  const renderLogsTab = () => {
    const rows = detailLogRows;
    const pageStart = rows.length
      ? (detailLogPagination.page - 1) * detailLogPagination.limit + 1
      : 0;
    const pageEnd = rows.length ? pageStart + rows.length - 1 : 0;

    return (
      <div className="enterprise-owners__detailGrid">
        <div className="enterprise-owners__detailCard">
          <div className="enterprise-owners__tabs enterprise-owners__tabs--secondary mb-3">
            {LOG_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`enterprise-owners__tabBtn ${detailLogTab === tab.key ? "is-active" : ""}`}
                onClick={() => handleLogTabChange(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="table-responsive">
            <div className="enterprise-owners__logsToolbar mb-3">
              <div className="enterprise-owners__logsPageSize">
                <span>Rows per page</span>
                <select value={detailLogPagination.limit} onChange={handleDetailLogPageSizeChange}>
                  {LOG_PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="enterprise-owners__uiBtn enterprise-owners__uiBtn--accent"
                onClick={() => exportLogs(detailLogTab)}
              >
                Export {toDisplayText(detailLogTab)}
              </button>
            </div>
            <table className="table table-sm table-borderless mb-0">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Event</th>
                  <th>Actor</th>
                  <th>Result</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {detailLogsLoading ? (
                  <tr>
                    <td colSpan={5} className="text-muted">
                      Loading activity log...
                    </td>
                  </tr>
                ) : null}
                {!detailLogsLoading && detailLogsError ? (
                  <tr>
                    <td colSpan={5} className="text-danger">
                      {detailLogsError}
                    </td>
                  </tr>
                ) : null}
                {rows.map((row) => {
                  const createdAt =
                    row.createdAt || row.sentAt || row.updatedAt || row.at || null;
                  const event = getEventLabel(row, detailLogTab);
                  const user = getLogUserLabel(row, detailLogTab);
                  const status = row.status || "ok";
                  const details = getLogDetailsText(row, detailLogTab);
                  const keySeed = row._id || `${event}-${createdAt || "na"}-${row.recipientEmail || row.actorEmail || ""}`;

                  return (
                    <tr key={keySeed}>
                      <td>{createdAt ? moment(createdAt).format("MMM DD, YYYY HH:mm") : "—"}</td>
                      <td>{event}</td>
                      <td>{user}</td>
                      <td>
                        <span className={`badge ${statusBadgeClass(status)}`}>{toDisplayText(status)}</span>
                      </td>
                      <td style={{ maxWidth: 420, whiteSpace: "pre-wrap" }}>{details}</td>
                    </tr>
                  );
                })}

                {!detailLogsLoading && !detailLogsError && !rows.length ? (
                  <tr>
                    <td colSpan={5} className="text-muted">
                      No activity recorded yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
            <div className="enterprise-owners__logsPagination">
              <div className="enterprise-owners__logsPaginationMeta">
                {detailLogPagination.total > 0
                  ? `Showing ${pageStart}-${pageEnd} of ${detailLogPagination.total}`
                  : "No activity records"}
              </div>
              <div className="enterprise-owners__logsPaginationActions">
                <button
                  type="button"
                  className="enterprise-owners__actionBtn"
                  disabled={detailLogsLoading || detailLogPagination.page <= 1}
                  onClick={() => goToDetailLogPage(detailLogPagination.page - 1)}
                >
                  Previous
                </button>
                <span>
                  Page {detailLogPagination.page} / {Math.max(detailLogPagination.totalPages, 1)}
                </span>
                <button
                  type="button"
                  className="enterprise-owners__actionBtn"
                  disabled={
                    detailLogsLoading || detailLogPagination.page >= detailLogPagination.totalPages
                  }
                  onClick={() => goToDetailLogPage(detailLogPagination.page + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPerformanceTab = () => {
    const report = performanceReport || performanceSummary?.report || null;
    const enterpriseOverview = report?.enterpriseOverview || {};
    const activityParticipation = report?.activityParticipation || {};
    const sessionPerformance = report?.sessionPerformance || {};
    const stageRows = report?.stagePerformance?.rows || [];
    const skillRows = report?.skillBehavior?.rows || [];
    const userRows = report?.userPerformance?.rows || [];
    const sessionInsightRows = report?.sessionInsights?.rows || [];
    const impactSummary = report?.impact || {};
    const growthRows = report?.growthTrends?.rows || [];
    const eventMetrics = report?.eventBasedMetrics || {};
    const peerFeedbackSummary = report?.peerFeedbackSummary || {};
    const chartData = report?.chartData || {};
    const weeklyActivityRows = chartData?.weeklyActivity || [];
    const participationDistribution = chartData?.participationDistribution || {};
    const sessionQualityDistribution = chartData?.sessionQualityDistribution || {};
    const participantReport = participantPerformanceData?.report || null;
    const participantSessionRows = participantReport?.sessionHistory?.rows || [];
    const participantSessionPagination = participantReport?.sessionHistory?.pagination || {};
    const participantParticipation = participantReport?.participationEngagement || {};
    const participantImpact = participantReport?.impact || {};
    const participantEventMetrics = participantReport?.eventBasedMetrics || {};
    const participantGrowthRows = participantReport?.growthOverTime?.rows || [];
    const participantPeerSummary = participantReport?.peerFeedbackSummary || {};
    const enterpriseGrowthLabels = growthRows.map((row) => row.period);
    const enterpriseGrowthValues = growthRows.map((row) => safeNumeric(row.decisionScore, 0));
    const weeklyLabels = weeklyActivityRows.map((row) => row.period);
    const weeklyCreated = weeklyActivityRows.map((row) => safeNumeric(row.sessionsCreated, 0));
    const weeklyCompleted = weeklyActivityRows.map((row) => safeNumeric(row.sessionsCompleted, 0));
    const weeklyActiveUsers = weeklyActivityRows.map((row) => safeNumeric(row.activeUsers, 0));
    const stageChartLabels = stageRows.map((row) => row.stage);
    const stageChartScores = stageRows.map((row) => asPercentValue(row.avgScore));

    const overviewCards = [
      { label: "Total Users", value: enterpriseOverview?.totalUsers ?? "—" },
      { label: "Active Users", value: enterpriseOverview?.activeUsers ?? "—" },
      { label: "Sessions Created", value: enterpriseOverview?.sessionsCreated ?? "—" },
      { label: "Sessions Completed", value: enterpriseOverview?.sessionsCompleted ?? "—" },
      { label: "Avg Participation", value: toPercentDisplay(enterpriseOverview?.avgParticipation) },
      { label: "Avg Decision Quality", value: toPercentDisplay(enterpriseOverview?.avgDecisionQuality) },
      { label: "Avg Alignment", value: toPercentDisplay(enterpriseOverview?.avgAlignment) },
      { label: "Reinforce Completion", value: toPercentDisplay(enterpriseOverview?.reinforceCompletion) },
    ];

    const activityCards = [
      { label: "Avg Participants / Session", value: toFixedDisplay(activityParticipation?.avgParticipantsPerSession, 2) },
      { label: "Feedback Completion", value: toPercentDisplay(activityParticipation?.feedbackCompletion) },
      { label: "Avg Response Time", value: secDisplay(activityParticipation?.avgResponseTimeSeconds) },
      { label: "Missed Feedback", value: activityParticipation?.missedFeedback ?? "—" },
    ];

    const sessionCards = [
      { label: "Avg Session Completion", value: toPercentDisplay(sessionPerformance?.avgSessionCompletion) },
      { label: "Avg Cycle Completion", value: toPercentDisplay(sessionPerformance?.avgCycleCompletion) },
      { label: "Avg Decision Score", value: toPercentDisplay(sessionPerformance?.avgDecisionScore) },
      { label: "Sessions Reaching Reinforce", value: sessionPerformance?.sessionsReachingReinforce ?? "—" },
    ];

    const eventMetricCards = buildOwnerEventMetricCards(eventMetrics);
    const metricCounterCards = buildOwnerMetricCountCards(eventMetrics);
    const renderParticipantPerformanceReport = () => {
      const activeParticipantName =
        participantReport?.profile?.userName ||
        selectedPerformanceUserName ||
        `User ${String(selectedPerformanceUserId || "").slice(-8)}`;
      const participationCards = [
        { label: "Attendance", value: toPercentDisplay(participantReport?.participationEngagement?.attendance) },
        { label: "Feedback Completion", value: toPercentDisplay(participantReport?.participationEngagement?.feedbackCompletion) },
        { label: "Avg Response Time", value: secDisplay(participantReport?.participationEngagement?.averageResponseTimeSeconds) },
        { label: "Missed Feedback", value: safeNumeric(participantReport?.participationEngagement?.missedFeedback, 0) },
        { label: "Reinforce Completion", value: toPercentDisplay(participantReport?.participationEngagement?.reinforceCompletion) },
      ];
      const impactCards = [
        { label: "Estimated Impact", value: toFixedDisplay(participantReport?.impact?.estimatedImpact, 2) },
        { label: "Estimated Confidence", value: toFixedDisplay(participantReport?.impact?.estimatedImpactConfidence, 2) },
        { label: "Actual Impact", value: toFixedDisplay(participantReport?.impact?.actualImpact, 2) },
        { label: "Gap", value: toFixedDisplay(participantReport?.impact?.gap, 2) },
        { label: "Accuracy Gap", value: toFixedDisplay(participantReport?.impact?.accuracyGap, 2) },
      ];
      const participantEventMetricCards = buildOwnerEventMetricCards(participantReport?.eventBasedMetrics || []);
      const participantMetricCounterCards = buildOwnerMetricCountCards(participantReport?.eventBasedMetrics || []);
      const participantSkillRows = participantReport?.skillBehavior?.rows || [];
      const participantSessionHistoryRows = participantReport?.sessionHistory?.rows || [];
      const participantDeepDiveRows = participantReport?.sessionDeepDive?.rows || [];
      const participantSessionHistoryById = new Map(
        participantSessionHistoryRows
          .filter((row) => String(row?.roomId || "").trim())
          .map((row) => [String(row.roomId), row])
      );
      const participantAllSessionRows = (participantAllSessions || [])
        .map((session) => {
          const roomId = String(session?.roomId || "");
          if (!roomId) return null;
          const sessionHistory = participantSessionHistoryById.get(roomId);
          const startedAt = session?.sessionStartedAt || null;
          const completedAt = session?.sessionCompletedAt || null;
          const startedAtMs = startedAt ? new Date(startedAt).getTime() : 0;
          const completedAtMs = completedAt ? new Date(completedAt).getTime() : 0;
          const runningDurationSeconds =
            startedAtMs > 0 && !completedAtMs
              ? Math.max(0, Math.round((Date.now() - startedAtMs) / 1000))
              : null;
          const fallbackDurationSeconds =
            startedAtMs > 0 && completedAtMs >= startedAtMs
              ? Math.round((completedAtMs - startedAtMs) / 1000)
              : runningDurationSeconds;
          const durationSeconds =
            safeNumeric(session?.totalTimeUsedSeconds, 0) > 0
              ? safeNumeric(session?.totalTimeUsedSeconds, 0)
              : fallbackDurationSeconds;
          const participantsCount =
            safeNumeric(session?.participantCount, 0) ||
            safeNumeric(session?.joinedParticipantCount, 0) ||
            (Array.isArray(session?.participantPerformance)
              ? session.participantPerformance.length
              : 0);
          return {
            roomId,
            sessionName: sessionHistory?.session || roomId || "—",
            decisionType:
              session?.decisionTypeName || session?.decisionType || session?.challengeType || "—",
            date: completedAt || startedAt || null,
            sortDateMs: completedAtMs || startedAtMs || 0,
            durationSeconds,
            status: session?.isPartial ? "Partial" : "Completed",
            participantsCount,
            facilitator: session?.leaderName || "—",
          };
        })
        .filter(Boolean)
        .sort((a, b) => b.sortDateMs - a.sortDateMs);

      return (
        <div className="enterprise-owners__participantInlinePanel" ref={participantReportRef}>
          <div className="enterprise-owners__participantHeader">
            <div>
              <h5 className="mb-1">Participant Performance Report</h5>
              <p className="enterprise-owners__participantMuted mb-0">
                Detailed participant performance report for the selected user.
              </p>
              <div className="enterprise-owners__drilldownNotice">
                Drilldown open for <strong>{activeParticipantName}</strong>
              </div>
            </div>
            <div className="enterprise-owners__performanceActions">
              <button
                type="button"
                className="enterprise-owners__uiBtn enterprise-owners__uiBtn--neutral"
                onClick={() => loadParticipantPerformance(selectedPerformanceUserId)}
                disabled={participantPerformanceLoading || participantPerformanceExporting}
              >
                {participantPerformanceLoading ? "Refreshing..." : "Refresh"}
              </button>
              <button
                type="button"
                className="enterprise-owners__uiBtn enterprise-owners__uiBtn--accent"
                onClick={exportParticipantPerformancePdf}
                disabled={participantPerformanceLoading || participantPerformanceExporting}
              >
                {participantPerformanceExporting ? "Preparing PDF..." : "Download Participant PDF"}
              </button>
              <button
                type="button"
                className="enterprise-owners__uiBtn enterprise-owners__uiBtn--neutral"
                onClick={() => {
                  setSelectedPerformanceUserId("");
                  setSelectedPerformanceUserName("");
                  setParticipantPerformanceData(null);
                  setParticipantPerformanceError("");
                  setParticipantAllSessions([]);
                  setParticipantAllSessionsError("");
                  setParticipantExpandedSessionId("");
                  setParticipantSessionMatrixCache({});
                }}
                disabled={participantPerformanceLoading || participantPerformanceExporting}
              >
                Close
              </button>
            </div>
          </div>

          {participantPerformanceLoading ? (
            <section
              className="enterprise-owners__participantLoader"
              role="status"
              aria-live="polite"
              aria-label="Loading participant performance report"
            >
              <div className="enterprise-owners__participantLoaderTop">
                <span className="enterprise-owners__participantLoaderLine enterprise-owners__participantLoaderLine--lg" />
                <span className="enterprise-owners__participantLoaderLine enterprise-owners__participantLoaderLine--md" />
              </div>

              <div className="enterprise-owners__participantLoaderMetrics">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div className="enterprise-owners__participantLoaderMetric" key={`participant-loader-metric-${index}`}>
                    <span className="enterprise-owners__participantLoaderLine enterprise-owners__participantLoaderLine--sm" />
                    <span className="enterprise-owners__participantLoaderLine enterprise-owners__participantLoaderLine--value" />
                  </div>
                ))}
              </div>

              <div className="enterprise-owners__participantLoaderTable">
                {Array.from({ length: 4 }).map((_, rowIndex) => (
                  <div className="enterprise-owners__participantLoaderTableRow" key={`participant-loader-row-${rowIndex}`}>
                    {Array.from({ length: 4 }).map((_, colIndex) => (
                      <span key={`participant-loader-cell-${rowIndex}-${colIndex}`} />
                    ))}
                  </div>
                ))}
              </div>

              <div className="enterprise-owners__participantLoaderCharts">
                <div className="enterprise-owners__participantLoaderChart" />
                <div className="enterprise-owners__participantLoaderChart" />
              </div>
            </section>
          ) : null}

          {!participantPerformanceLoading && participantPerformanceError ? (
            <p className="text-danger mb-0">{participantPerformanceError}</p>
          ) : null}

          {!participantPerformanceLoading && participantReport ? (
            <>
              <section className="enterprise-owners__participantBlock">
                <h6 className="enterprise-owners__participantTitle">1. Profile & Snapshot</h6>
                <div className="enterprise-owners__participantTableWrap">
                  <table className="table table-sm mb-0">
                    <tbody>
                      <tr><th>User Name</th><td>{participantReport?.profile?.userName || "—"}</td><th>Sessions</th><td>{participantReport?.profile?.sessions || 0}</td></tr>
                      <tr><th>As Leader</th><td>{participantReport?.profile?.asLeader || 0}</td><th>As Peer</th><td>{participantReport?.profile?.asPeer || 0}</td></tr>
                      <tr><th>Last Active</th><td colSpan={3}>{participantReport?.profile?.lastActive ? formatDate(participantReport?.profile?.lastActive, "MMM DD, YYYY HH:mm") : "—"}</td></tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="enterprise-owners__participantBlock">
                <h6 className="enterprise-owners__participantTitle">2. Overall Performance Score</h6>
                <div className="enterprise-owners__participantMetricGrid">
                  <div className="enterprise-owners__participantMetricCard">
                    <span>Performance Score</span>
                    <strong>{toFixedDisplay(participantReport?.overallPerformance?.score, 2)} / 100</strong>
                  </div>
                  <div className="enterprise-owners__participantMetricCard">
                    <span>Meaning</span>
                    <strong>{participantReport?.overallPerformance?.meaning || "—"}</strong>
                  </div>
                </div>
              </section>

              <section className="enterprise-owners__participantBlock">
                <h6 className="enterprise-owners__participantTitle">3. Role Performance</h6>
                <div className="enterprise-owners__participantTableWrap">
                  <table className="table table-sm mb-0">
                    <thead><tr><th>Role</th><th>Metric</th><th>Value</th></tr></thead>
                    <tbody>
                      <tr><td>As Leader</td><td>Sessions Led</td><td>{participantReport?.rolePerformance?.asLeader?.sessionsLed || 0}</td></tr>
                      <tr><td>As Leader</td><td>Decision Success</td><td>{toPercentDisplay(participantReport?.rolePerformance?.asLeader?.decisionSuccess)}</td></tr>
                      <tr><td>As Leader</td><td>Peer Alignment</td><td>{toPercentDisplay(participantReport?.rolePerformance?.asLeader?.peerAlignment)}</td></tr>
                      <tr><td>As Leader</td><td>Stage Completion</td><td>{toPercentDisplay(participantReport?.rolePerformance?.asLeader?.stageCompletion)}</td></tr>
                      <tr><td>As Peer</td><td>Feedback Completion</td><td>{toPercentDisplay(participantReport?.rolePerformance?.asPeer?.feedbackCompletion)}</td></tr>
                      <tr><td>As Peer</td><td>Alignment Accuracy</td><td>{toPercentDisplay(participantReport?.rolePerformance?.asPeer?.alignmentAccuracy)}</td></tr>
                      <tr><td>As Peer</td><td>Recommendation Success</td><td>{toPercentDisplay(participantReport?.rolePerformance?.asPeer?.recommendationSuccess)}</td></tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="enterprise-owners__participantBlock">
                <h6 className="enterprise-owners__participantTitle">4. Participation & Engagement</h6>
                <div className="enterprise-owners__participantMetricGrid">
                  {participationCards.map((card) => (
                    <div className="enterprise-owners__participantMetricCard" key={`pm-${card.label}`}>
                      <span>{card.label}</span>
                      <strong>{card.value}</strong>
                    </div>
                  ))}
                </div>
              </section>

              <section className="enterprise-owners__participantBlock">
                <h6 className="enterprise-owners__participantTitle">10. Impact</h6>
                <div className="enterprise-owners__participantMetricGrid">
                  {impactCards.map((card) => (
                    <div className="enterprise-owners__participantMetricCard" key={`pi-${card.label}`}>
                      <span>{card.label}</span>
                      <strong>{card.value}</strong>
                    </div>
                  ))}
                </div>
              </section>

              <section className="enterprise-owners__participantBlock">
                <h6 className="enterprise-owners__participantTitle">4A. Event-Based Metrics</h6>
                <div className="enterprise-owners__participantMetricGrid">
                  {participantEventMetricCards.map((item) => (
                    <div className="enterprise-owners__participantMetricCard" key={`pem-${item.label}`}>
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              </section>

              <section className="enterprise-owners__participantBlock">
                <h6 className="enterprise-owners__participantTitle">4B. Metric Counters</h6>
                <div className="enterprise-owners__participantMetricGrid">
                  {participantMetricCounterCards.map((item) => (
                    <div className="enterprise-owners__participantMetricCard" key={`pmc-${item.label}`}>
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              </section>

              <section className="enterprise-owners__participantBlock">
                <h6 className="enterprise-owners__participantTitle">5. Stage Performance</h6>
                <div className="enterprise-owners__participantTableWrap">
                  <table className="table table-sm mb-0">
                    <thead><tr><th>Stage</th><th>Expected Cycles</th><th>Completed Cycles</th><th>Pending Cycles</th><th>Advance Cycles</th><th>Score</th></tr></thead>
                    <tbody>
                      {(participantReport?.stagePerformance?.rows || []).map((row) => {
                        const expected = { Define: 2, Own: 2, Enlist: 2, Resolve: 2, Reinforce: 1 }[row.stage] ?? null;
                        const completed = safeNumeric(row.totalCycles ?? row.cyclesCompleted, 0);
                        return (
                          <tr key={`ps-${row.stage}`}>
                            <td>{row.stage}</td>
                            <td>{expected === null ? "—" : expected}</td>
                            <td>{completed}</td>
                            <td>{expected === null ? "—" : Math.max(expected - completed, 0)}</td>
                            <td>{safeNumeric(row.advanceCycles, 0)}</td>
                            <td>{toPercentDisplay(row.score)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="enterprise-owners__participantBlock">
                <h6 className="enterprise-owners__participantTitle">6. Skill Behavior (Leader)</h6>
                <div className="enterprise-owners__participantTableWrap">
                  <table className="table table-sm mb-0">
                    <thead><tr><th>Skill</th><th>Usage</th><th>Success</th><th>Success Count</th><th>Not-Advance Count</th></tr></thead>
                    <tbody>
                      {participantSkillRows.map((row) => {
                        const usage = safeNumeric(row.usage, 0);
                        const successCount = Math.max(0, Math.min(usage, Math.round((usage * safeNumeric(row.success, 0)) / 100)));
                        return (
                          <tr key={`pk-${row.skill}`}>
                            <td>{row.skill}</td>
                            <td>{usage}</td>
                            <td>{toPercentDisplay(row.success)}</td>
                            <td>{successCount}</td>
                            <td>{Math.max(usage - successCount, 0)}</td>
                          </tr>
                        );
                      })}
                      {!participantSkillRows.length ? (
                        <tr>
                          <td colSpan={5} className="enterprise-owners__participantEmpty">No skill behavior data available.</td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="enterprise-owners__participantBlock">
                <h6 className="enterprise-owners__participantTitle">7. Session History</h6>
                <div className="enterprise-owners__participantTableWrap">
                  <table className="table table-sm mb-0">
                    <thead><tr><th>Session</th><th>Role</th><th>Decision</th><th>Alignment</th><th>Impact Gap</th></tr></thead>
                    <tbody>
                      {participantSessionHistoryRows.map((row) => (
                        <tr key={`ph-${row.roomId || row.session}`}>
                          <td>{row.session || "—"}</td>
                          <td>{row.role || "—"}</td>
                          <td>{toPercentDisplay(row.decision)}</td>
                          <td>{toPercentDisplay(row.alignment)}</td>
                          <td>{toFixedDisplay(row.impactGap, 2)}</td>
                        </tr>
                      ))}
                      {!participantSessionHistoryRows.length ? (
                        <tr>
                          <td colSpan={5} className="enterprise-owners__participantEmpty">No session history available.</td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="enterprise-owners__participantBlock">
                <h6 className="enterprise-owners__participantTitle">8. Session Deep Dive</h6>
                <div className="enterprise-owners__participantTableWrap">
                  <table className="table table-sm mb-0">
                    <thead><tr><th>Cycle</th><th>Stage</th><th>Leader Skill</th><th>Your Selected Skill</th><th>Your Next Move</th><th>Alignment</th><th>Advance Signal</th></tr></thead>
                    <tbody>
                      {participantDeepDiveRows.map((row, idx) => (
                        <tr key={`pd-${idx}`}>
                          <td>{row.cycle ?? "—"}</td>
                          <td>{row.stage || "—"}</td>
                          <td>{row.leaderSkill || row.skill || "—"}</td>
                          <td>{row.selectedSkill || row.skill || "—"}</td>
                          <td>{row.nextMove || "—"}</td>
                          <td>{row.alignment || "—"}</td>
                          <td>{row.advance || "—"}</td>
                        </tr>
                      ))}
                      {!participantDeepDiveRows.length ? (
                        <tr>
                          <td colSpan={7} className="enterprise-owners__participantEmpty">No cycle rows available for this session.</td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="enterprise-owners__participantBlock">
                <h6 className="enterprise-owners__participantTitle">9. Show All Sessions</h6>
                <div className="mb-2">
                  <button
                    type="button"
                    className="enterprise-owners__uiBtn enterprise-owners__uiBtn--neutral"
                    onClick={downloadParticipantSessionsPdf}
                    disabled={participantAllSessionsLoading || participantSessionsPdfLoading}
                  >
                    {participantSessionsPdfLoading ? "Preparing PDF..." : "Download PDF"}
                  </button>
                </div>
                {participantAllSessionsError ? (
                  <p className="text-danger mb-2">{participantAllSessionsError}</p>
                ) : null}
                <div className="enterprise-owners__participantTableWrap">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Session Name</th>
                        <th>Decision Type</th>
                        <th>Date</th>
                        <th>Duration</th>
                        <th>Status</th>
                        <th>Participants Count</th>
                        <th>Facilitator / Leader</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {participantAllSessionsLoading ? (
                        <tr>
                          <td colSpan={8} className="enterprise-owners__participantEmpty">
                            Loading sessions...
                          </td>
                        </tr>
                      ) : null}
                      {!participantAllSessionsLoading && !participantAllSessionRows.length ? (
                        <tr>
                          <td colSpan={8} className="enterprise-owners__participantEmpty">
                            No sessions found for this user.
                          </td>
                        </tr>
                      ) : null}
                      {!participantAllSessionsLoading
                        ? participantAllSessionRows.map((row) => {
                            const details = getParticipantSessionMatrixDetail(row.roomId);
                            const isExpanded = participantExpandedSessionId === row.roomId;
                            const maxPeerColumns = Math.max(1, Number(details?.maxPeerColumns || 0));
                            return (
                              <React.Fragment key={`pas-${row.roomId}`}>
                                <tr>
                                  <td>{row.sessionName || "—"}</td>
                                  <td>{row.decisionType || "—"}</td>
                                  <td>{formatSessionDateTime(row.date)}</td>
                                  <td>{formatSessionDuration(row.durationSeconds)}</td>
                                  <td>{row.status || "—"}</td>
                                  <td>{safeNumeric(row.participantsCount, 0)}</td>
                                  <td>{row.facilitator || "—"}</td>
                                  <td>
                                    <button
                                      type="button"
                                      className="enterprise-owners__uiBtn enterprise-owners__uiBtn--neutral"
                                      onClick={() =>
                                        toggleParticipantSessionExpansion({
                                          userId: selectedPerformanceUserId,
                                          roomId: row.roomId,
                                        })
                                      }
                                    >
                                      {isExpanded ? "Collapse" : "View Details"}
                                    </button>
                                  </td>
                                </tr>
                                {isExpanded ? (
                                  <tr>
                                    <td colSpan={8} className="enterprise-owners__participantDetailCell">
                                      {details?.status === "loading" ? (
                                        <p className="enterprise-owners__participantEmpty mb-0">
                                          Loading session details...
                                        </p>
                                      ) : null}
                                      {details?.status === "error" ? (
                                        <p className="text-danger mb-0">{details.error}</p>
                                      ) : null}
                                      {details?.status === "loaded" && !details?.rows?.length ? (
                                        <p className="enterprise-owners__participantEmpty mb-0">
                                          No detailed rows found for this session.
                                        </p>
                                      ) : null}
                                      {details?.status === "loaded" && details?.rows?.length ? (
                                        <div className="enterprise-owners__participantNestedTableWrap">
                                          <table className="table table-sm mb-0">
                                            <thead>
                                              <tr>
                                                <th>Stage</th>
                                                <th>Cycle</th>
                                                <th>Leader Intent</th>
                                                <th>Leader Message</th>
                                                {Array.from({ length: maxPeerColumns }).map((_, index) => (
                                                  <React.Fragment key={`admin-peer-head-${row.roomId}-${index}`}>
                                                    <th>{`Peer ${index + 1} Posture`}</th>
                                                    <th>{`Peer ${index + 1} Move`}</th>
                                                  </React.Fragment>
                                                ))}
                                                <th>Recommended Next Posture</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {details.rows.map((detailRow) => {
                                                const detailRowKey =
                                                  detailRow?.rowId ||
                                                  `${row.roomId}-${detailRow?.stage || "stage"}-${detailRow?.cycle || "cycle"}`;
                                                return (
                                                  <tr key={detailRowKey}>
                                                    <td>{detailRow?.stage || "-"}</td>
                                                    <td>{detailRow?.cycle ?? "-"}</td>
                                                    <td>{detailRow?.leaderIntent || "-"}</td>
                                                    <td>{detailRow?.leaderMessage || "-"}</td>
                                                    {Array.from({ length: maxPeerColumns }).map((_, peerIndex) => (
                                                      <React.Fragment key={`admin-peer-row-${detailRowKey}-${peerIndex}`}>
                                                        <td>{detailRow?.peers?.[peerIndex]?.posture || "-"}</td>
                                                        <td>{detailRow?.peers?.[peerIndex]?.move || "-"}</td>
                                                      </React.Fragment>
                                                    ))}
                                                    <td>{detailRow?.recommendedNextPosture || "-"}</td>
                                                  </tr>
                                                );
                                              })}
                                            </tbody>
                                          </table>
                                        </div>
                                      ) : null}
                                    </td>
                                  </tr>
                                ) : null}
                              </React.Fragment>
                            );
                          })
                        : null}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="enterprise-owners__participantBlock enterprise-owners__participantBlock--chart">
                <div className="enterprise-owners__reportHeader">
                  <h5>Performance Visualization Deck</h5>
                  <span>Session progression, role comparison, stage strength, skill outcomes, and impact.</span>
                </div>
                <div className="enterprise-owners__chartGrid enterprise-owners__chartGrid--double mt-3">
                  <div className="enterprise-owners__chartCard">
                    <div className="enterprise-owners__chartTitle">Performance Trend</div>
                    <div className="enterprise-owners__chartBody">
                      <Line data={{ labels: (participantReport?.growthOverTime?.rows || []).map((r, i) => r.session || `S${i + 1}`), datasets: [{ label: "Score", data: (participantReport?.growthOverTime?.rows || []).map((r) => safeNumeric(r.score, 0)), borderColor: chartPalette.blue, backgroundColor: chartPalette.blueSoft, fill: true, tension: 0.35 }] }} options={{ ...baseLineOptions, scales: { ...baseLineOptions.scales, y: { ...baseLineOptions.scales.y, max: 100 } } }} />
                    </div>
                  </div>
                  <div className="enterprise-owners__chartCard">
                    <div className="enterprise-owners__chartTitle">Leader vs Peer Performance</div>
                    <div className="enterprise-owners__chartBody">
                      <Bar data={{ labels: ["Decision", "Alignment", "Feedback", "Recommendation"], datasets: [{ label: "Leader", data: [safeNumeric(participantReport?.rolePerformance?.asLeader?.decisionSuccess, 0), safeNumeric(participantReport?.rolePerformance?.asLeader?.peerAlignment, 0), safeNumeric(participantReport?.rolePerformance?.asLeader?.stageCompletion, 0), 0], backgroundColor: chartPalette.blueSoft, borderColor: chartPalette.blue, borderWidth: 1 }, { label: "Peer", data: [safeNumeric(participantReport?.peerFeedbackSummary?.advance, 0), safeNumeric(participantReport?.rolePerformance?.asPeer?.alignmentAccuracy, 0), safeNumeric(participantReport?.rolePerformance?.asPeer?.feedbackCompletion, 0), safeNumeric(participantReport?.rolePerformance?.asPeer?.recommendationSuccess, 0)], backgroundColor: chartPalette.greenSoft, borderColor: chartPalette.green, borderWidth: 1 }] }} options={{ ...baseLineOptions, scales: { ...baseLineOptions.scales, y: { ...baseLineOptions.scales.y, max: 100 } } }} />
                    </div>
                  </div>
                  <div className="enterprise-owners__chartCard">
                    <div className="enterprise-owners__chartTitle">Stage Performance</div>
                    <div className="enterprise-owners__chartBody">
                      <Bar data={{ labels: (participantReport?.stagePerformance?.rows || []).map((r) => r.stage), datasets: [{ label: "Stage Score (%)", data: (participantReport?.stagePerformance?.rows || []).map((r) => asPercentValue(r.score)), backgroundColor: chartPalette.yellowSoft, borderColor: chartPalette.yellow, borderWidth: 1, borderRadius: 8 }] }} options={{ ...baseLineOptions, scales: { ...baseLineOptions.scales, y: { ...baseLineOptions.scales.y, max: 100 } } }} />
                    </div>
                  </div>
                  <div className="enterprise-owners__chartCard">
                    <div className="enterprise-owners__chartTitle">Leader Skill Usage & Success</div>
                    <div className="enterprise-owners__chartBody">
                      <Bar data={{ labels: participantSkillRows.slice(0, 8).map((r) => r.skill), datasets: [{ label: "Usage", data: participantSkillRows.slice(0, 8).map((r) => safeNumeric(r.usage, 0)), backgroundColor: chartPalette.blueSoft, borderColor: chartPalette.blue, borderWidth: 1 }, { label: "Success (%)", data: participantSkillRows.slice(0, 8).map((r) => safeNumeric(r.success, 0)), backgroundColor: chartPalette.greenSoft, borderColor: chartPalette.green, borderWidth: 1 }] }} options={baseLineOptions} />
                    </div>
                  </div>
                  <div className="enterprise-owners__chartCard">
                    <div className="enterprise-owners__chartTitle">Impact Comparison</div>
                    <div className="enterprise-owners__chartBody">
                      <Bar data={{ labels: ["Impact"], datasets: [{ label: "Estimated", data: [safeNumeric(participantReport?.impact?.estimatedImpact, 0)], backgroundColor: chartPalette.blueSoft, borderColor: chartPalette.blue, borderWidth: 1 }, { label: "Actual", data: [safeNumeric(participantReport?.impact?.actualImpact, 0)], backgroundColor: chartPalette.greenSoft, borderColor: chartPalette.green, borderWidth: 1 }] }} options={baseLineOptions} />
                    </div>
                  </div>
                </div>
              </section>
            </>
          ) : null}
        </div>
      );
    };

    return (
      <div className="enterprise-owners__detailGrid">
        <div className="enterprise-owners__detailCard enterprise-owners__detailCard--performanceReport">
          <div className="enterprise-owners__reportCanvas">
            <section className="enterprise-owners__reportUnifiedCard">
              <div className="enterprise-owners__reportSectionHeader">
                <div>
                  <h5 className="enterprise-owners__reportTitleMain mb-1">Enterprise Performance Report</h5>
                  <p className="enterprise-owners__reportMuted mb-0">
                    Leadership view of decision quality, alignment, execution, and outcome reliability.
                  </p>
                  {performanceLastRebuiltAt ? (
                    <div className="enterprise-owners__sectionHint mt-2 mb-0">
                      {`Last rebuild: ${moment(performanceLastRebuiltAt).format("MMM DD, YYYY HH:mm")}`}
                    </div>
                  ) : null}
                </div>
                <div className="enterprise-owners__reportActions">
                  <select
                    className="form-control enterprise-owners__select enterprise-owners__reportInputInline"
                    value={performanceWindow}
                    onChange={(event) => setPerformanceWindow(event.target.value)}
                  >
                    <option value="all">All Time</option>
                    <option value="7d">Last 7 Days</option>
                    <option value="30d">Last 30 Days</option>
                  </select>
                  <button
                    type="button"
                    className="enterprise-owners__uiBtn enterprise-owners__uiBtn--neutral"
                    onClick={() => loadPerformance()}
                    disabled={performanceLoading || performanceRebuilding}
                  >
                    {performanceLoading ? "Refreshing..." : "Refresh"}
                  </button>
                  {/* <button
                    type="button"
                    className="enterprise-owners__uiBtn enterprise-owners__uiBtn--neutral"
                    onClick={rebuildPerformanceSnapshots}
                    disabled={
                      performanceLoading ||
                      performanceExporting ||
                      performanceRebuilding ||
                      performanceGlobalRebuilding
                    }
                  >
                    {performanceRebuilding ? "Rebuilding..." : "Rebuild Snapshots"}
                  </button> */}
                  {/* <button
                    type="button"
                    className="enterprise-owners__uiBtn enterprise-owners__uiBtn--neutral"
                    onClick={rebuildAllPerformanceSnapshots}
                    disabled={
                      performanceLoading ||
                      performanceExporting ||
                      performanceRebuilding ||
                      performanceGlobalRebuilding
                    }
                  >
                    {performanceGlobalRebuilding ? "Rebuilding All..." : "Rebuild All Enterprises"}
                  </button> */}
                  <button
                    type="button"
                    className="enterprise-owners__uiBtn enterprise-owners__uiBtn--accent"
                    onClick={exportPerformancePdf}
                    disabled={
                      performanceExporting ||
                      performanceLoading ||
                      performanceRebuilding ||
                      performanceGlobalRebuilding
                    }
                  >
                    {performanceExporting ? "Preparing PDF..." : "Download Enterprise PDF"}
                  </button>
                </div>
              </div>

              {performanceError ? (
                <div className="enterprise-owners__stateCard enterprise-owners__stateCard--error mt-3">
                  <strong>Performance data unavailable</strong>
                  <p>{performanceError}</p>
                </div>
              ) : null}

              {performanceLoading ? (
                <div
                  className="enterprise-owners__reportLoader mt-3"
                  role="status"
                  aria-live="polite"
                  aria-label="Loading enterprise performance report"
                >
                  <div className="enterprise-owners__reportLoaderTop">
                    <div className="enterprise-owners__reportLoaderLine enterprise-owners__reportLoaderLine--xl" />
                    <div className="enterprise-owners__reportLoaderLine enterprise-owners__reportLoaderLine--lg" />
                    <div className="enterprise-owners__reportLoaderChips">
                      <span className="enterprise-owners__reportLoaderChip" />
                      <span className="enterprise-owners__reportLoaderChip" />
                      <span className="enterprise-owners__reportLoaderChip" />
                    </div>
                  </div>

                  <div className="enterprise-owners__reportLoaderCards">
                    {Array.from({ length: 8 }).map((_, index) => (
                      <div className="enterprise-owners__reportLoaderCard" key={`loader-card-${index}`}>
                        <div className="enterprise-owners__reportLoaderLine enterprise-owners__reportLoaderLine--sm" />
                        <div className="enterprise-owners__reportLoaderLine enterprise-owners__reportLoaderLine--md" />
                      </div>
                    ))}
                  </div>

                  <div className="enterprise-owners__reportLoaderTable">
                    <div className="enterprise-owners__reportLoaderTableHead">
                      {Array.from({ length: 5 }).map((_, index) => (
                        <span key={`loader-th-${index}`} />
                      ))}
                    </div>
                    <div className="enterprise-owners__reportLoaderTableBody">
                      {Array.from({ length: 4 }).map((_, row) => (
                        <div className="enterprise-owners__reportLoaderTableRow" key={`loader-row-${row}`}>
                          {Array.from({ length: 5 }).map((_, col) => (
                            <span key={`loader-td-${row}-${col}`} />
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="enterprise-owners__reportLoaderCharts">
                    <div className="enterprise-owners__reportLoaderChart" />
                    <div className="enterprise-owners__reportLoaderChart" />
                  </div>
                </div>
              ) : null}

              {!performanceLoading ? (
                <>
              <section className="enterprise-owners__reportBlock">
                <h6 className="enterprise-owners__reportBlockTitle">Enterprise Overview</h6>
                <div className="enterprise-owners__summaryGrid">
                  {overviewCards.map((card) => (
                    <div className="enterprise-owners__summaryCard" key={card.label}>
                      <span>{card.label}</span>
                      <strong>{card.value}</strong>
                    </div>
                  ))}
                </div>
              </section>

              <section className="enterprise-owners__reportBlock">
                <h6 className="enterprise-owners__reportBlockTitle">Activity & Participation</h6>
                <div className="enterprise-owners__summaryGrid">
                  {activityCards.map((card) => (
                    <div className="enterprise-owners__summaryCard" key={card.label}>
                      <span>{card.label}</span>
                      <strong>{card.value}</strong>
                    </div>
                  ))}
                </div>
              </section>

              <section className="enterprise-owners__reportBlock">
                <h6 className="enterprise-owners__reportBlockTitle">Event-Based Metrics</h6>
                <div className="enterprise-owners__summaryGrid">
                  {eventMetricCards.map((card) => (
                    <div className="enterprise-owners__summaryCard" key={card.label}>
                      <span>{card.label}</span>
                      <strong>{card.value}</strong>
                    </div>
                  ))}
                </div>
              </section>

              <section className="enterprise-owners__reportBlock">
                <h6 className="enterprise-owners__reportBlockTitle">Metric Counters</h6>
                <div className="enterprise-owners__summaryGrid">
                  {metricCounterCards.map((card) => (
                    <div className="enterprise-owners__summaryCard" key={card.label}>
                      <span>{card.label}</span>
                      <strong>{card.value}</strong>
                    </div>
                  ))}
                </div>
              </section>

              <section className="enterprise-owners__reportBlock">
                <h6 className="enterprise-owners__reportBlockTitle">Session Performance</h6>
                <div className="enterprise-owners__summaryGrid">
                  {sessionCards.map((card) => (
                    <div className="enterprise-owners__summaryCard" key={card.label}>
                      <span>{card.label}</span>
                      <strong>{card.value}</strong>
                    </div>
                  ))}
                </div>
              </section>

              <section className="enterprise-owners__reportBlock enterprise-owners__reportBlock--chart">
                <div className="enterprise-owners__reportHeader">
                  <h5 className="mb-0">Performance Deck</h5>
                  <span>Calibrated enterprise signals for participation, decision movement, and execution quality.</span>
                </div>
                <div className="enterprise-owners__chartGrid enterprise-owners__chartGrid--double mt-3">
            <div className="enterprise-owners__chartCard">
              <div className="enterprise-owners__chartTitle">Enterprise Growth Trend</div>
              <div className="enterprise-owners__chartBody">
                <Line
                  data={{
                    labels: enterpriseGrowthLabels,
                    datasets: [
                      {
                        label: "Decision Score",
                        data: enterpriseGrowthValues,
                        borderColor: chartPalette.blue,
                        backgroundColor: chartPalette.blueSoft,
                        borderWidth: 2,
                        tension: 0.35,
                        fill: true,
                      },
                    ],
                  }}
                  options={{
                    ...baseLineOptions,
                    scales: {
                      ...baseLineOptions.scales,
                      y: {
                        ...baseLineOptions.scales.y,
                        max: 100,
                      },
                    },
                  }}
                />
              </div>
            </div>
            <div className="enterprise-owners__chartCard">
              <div className="enterprise-owners__chartTitle">Weekly Throughput</div>
              <div className="enterprise-owners__chartBody">
                <Bar
                  data={{
                    labels: weeklyLabels,
                    datasets: [
                      {
                        label: "Created",
                        data: weeklyCreated,
                        backgroundColor: chartPalette.blueSoft,
                        borderColor: chartPalette.blue,
                        borderWidth: 1,
                        borderRadius: 8,
                      },
                      {
                        label: "Completed",
                        data: weeklyCompleted,
                        backgroundColor: chartPalette.greenSoft,
                        borderColor: chartPalette.green,
                        borderWidth: 1,
                        borderRadius: 8,
                      },
                    ],
                  }}
                  options={baseLineOptions}
                />
              </div>
            </div>
            <div className="enterprise-owners__chartCard">
              <div className="enterprise-owners__chartTitle">Active Users Pulse</div>
              <div className="enterprise-owners__chartBody">
                <Line
                  data={{
                    labels: weeklyLabels,
                    datasets: [
                      {
                        label: "Active Users",
                        data: weeklyActiveUsers,
                        borderColor: chartPalette.cyan,
                        backgroundColor: "rgba(15, 118, 110, 0.15)",
                        borderWidth: 2,
                        tension: 0.35,
                        fill: true,
                      },
                    ],
                  }}
                  options={baseLineOptions}
                />
              </div>
            </div>
            <div className="enterprise-owners__chartCard">
              <div className="enterprise-owners__chartTitle">Stage Performance</div>
              <div className="enterprise-owners__chartBody">
                <Bar
                  data={{
                    labels: stageChartLabels,
                    datasets: [
                      {
                        label: "Stage Score (%)",
                        data: stageChartScores,
                        backgroundColor: chartPalette.yellowSoft,
                        borderColor: chartPalette.yellow,
                        borderWidth: 1,
                        borderRadius: 8,
                      },
                    ],
                  }}
                  options={{
                    ...baseLineOptions,
                    scales: {
                      ...baseLineOptions.scales,
                      y: {
                        ...baseLineOptions.scales.y,
                        max: 100,
                      },
                    },
                  }}
                />
              </div>
            </div>
            <div className="enterprise-owners__chartCard">
              <div className="enterprise-owners__chartTitle">Skill Usage & Effectiveness</div>
              <div className="enterprise-owners__chartBody">
                <Bar
                  data={{
                    labels: skillRows.slice(0, 10).map((row) => row.skill),
                    datasets: [
                      {
                        label: "Usage",
                        data: skillRows.slice(0, 10).map((row) => safeNumeric(row.usage, 0)),
                        backgroundColor: chartPalette.blueSoft,
                        borderColor: chartPalette.blue,
                        borderWidth: 1,
                      },
                      {
                        label: "Success %",
                        data: skillRows.slice(0, 10).map((row) => safeNumeric(row.success, 0)),
                        backgroundColor: chartPalette.greenSoft,
                        borderColor: chartPalette.green,
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={baseLineOptions}
                />
              </div>
            </div>
            <div className="enterprise-owners__chartCard">
              <div className="enterprise-owners__chartTitle">Impact & Reinforce</div>
              <div className="enterprise-owners__chartBody">
                <Line
                  data={{
                    labels: weeklyLabels,
                    datasets: [
                      {
                        label: "Estimated",
                        data: weeklyActivityRows.map((row) => safeNumeric(row.avgEstimatedImpact, 0)),
                        borderColor: chartPalette.blue,
                        backgroundColor: chartPalette.blueSoft,
                        borderWidth: 2,
                      },
                      {
                        label: "Actual",
                        data: weeklyActivityRows.map((row) => safeNumeric(row.avgActualImpact, 0)),
                        borderColor: chartPalette.green,
                        backgroundColor: chartPalette.greenSoft,
                        borderWidth: 2,
                      },
                      {
                        label: "Reinforce %",
                        data: weeklyActivityRows.map((row) => safeNumeric(row.reinforceCompletion, 0)),
                        borderColor: chartPalette.yellow,
                        backgroundColor: chartPalette.yellowSoft,
                        borderWidth: 2,
                      },
                    ],
                  }}
                  options={baseLineOptions}
                />
              </div>
            </div>
            <div className="enterprise-owners__chartCard">
              <div className="enterprise-owners__chartTitle">Session Quality Mix</div>
              <div className="enterprise-owners__chartBody enterprise-owners__chartBody--donut">
                <Doughnut
                  data={{
                    labels: ["Strong", "Moderate", "Weak"],
                    datasets: [
                      {
                        data: [
                          safeNumeric(sessionQualityDistribution?.strong, 0),
                          safeNumeric(sessionQualityDistribution?.moderate, 0),
                          safeNumeric(sessionQualityDistribution?.weak, 0),
                        ],
                        backgroundColor: [chartPalette.green, chartPalette.yellow, chartPalette.red],
                        borderWidth: 0,
                      },
                    ],
                  }}
                  options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "bottom" } } }}
                />
              </div>
            </div>
            <div className="enterprise-owners__chartCard">
              <div className="enterprise-owners__chartTitle">Peer Signal Health</div>
              <div className="enterprise-owners__chartBody">
                <Bar
                  data={{
                    labels: ["Peer Health"],
                    datasets: [
                      { label: "Aligned", data: [safeNumeric(peerFeedbackSummary?.alignment, 0)], backgroundColor: chartPalette.green },
                      { label: "Not Aligned", data: [Math.max(0, 100 - safeNumeric(peerFeedbackSummary?.alignment, 0))], backgroundColor: chartPalette.red },
                      { label: "Advance", data: [safeNumeric(peerFeedbackSummary?.advance, 0)], backgroundColor: chartPalette.blue },
                      { label: "Not Advance", data: [safeNumeric(peerFeedbackSummary?.notAdvance, 0)], backgroundColor: chartPalette.yellow },
                    ],
                  }}
                  options={{
                    ...baseLineOptions,
                    scales: {
                      ...baseLineOptions.scales,
                      y: { ...baseLineOptions.scales.y, max: 100 },
                    },
                  }}
                />
              </div>
            </div>
                </div>
              </section>

              <section className="enterprise-owners__reportBlock">
                <h6 className="enterprise-owners__reportBlockTitle">Stage Performance</h6>
                <div className="enterprise-owners__reportTableWrap">
                  <table className="enterprise-owners__reportTable">
                    <thead>
                      <tr>
                        <th>Stage</th>
                        <th>Total Cycles</th>
                        <th>Advance Cycles</th>
                        <th>Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stageRows.map((row) => (
                        <tr key={`stage-row-${row.stage}`}>
                          <td>{row.stage}</td>
                          <td>{safeNumeric(row.totalCycles, 0)}</td>
                          <td>{safeNumeric(row.advanceCycles, 0)}</td>
                          <td>{toPercentDisplay(row.avgScore)}</td>
                        </tr>
                      ))}
                      {!stageRows.length ? (
                        <tr>
                          <td colSpan={4} className="enterprise-owners__reportEmpty">
                            No stage performance data.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="enterprise-owners__reportBlock">
                <h6 className="enterprise-owners__reportBlockTitle">Skill Behavior</h6>
                <div className="enterprise-owners__reportTableWrap">
                  <table className="enterprise-owners__reportTable">
                    <thead>
                      <tr>
                        <th>Skill</th>
                        <th>Usage</th>
                        <th>Success</th>
                      </tr>
                    </thead>
                    <tbody>
                      {skillRows.map((row) => (
                        <tr key={`skill-row-${row.skill}`}>
                          <td>{row.skill}</td>
                          <td>{safeNumeric(row.usage, 0)}</td>
                          <td>{toPercentDisplay(row.success)}</td>
                        </tr>
                      ))}
                      {!skillRows.length ? (
                        <tr>
                          <td colSpan={3} className="enterprise-owners__reportEmpty">
                            No skill behavior data.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="enterprise-owners__reportBlock">
                <h6 className="enterprise-owners__reportBlockTitle">Impact</h6>
                <div className="enterprise-owners__summaryGrid">
                  <div className="enterprise-owners__summaryCard">
                    <span>Avg Estimated Impact</span>
                    <strong>{toFixedDisplay(impactSummary?.avgEstimatedImpact, 2)}</strong>
                  </div>
                  <div className="enterprise-owners__summaryCard">
                    <span>Avg Actual Impact</span>
                    <strong>{toFixedDisplay(impactSummary?.avgActualImpact, 2)}</strong>
                  </div>
                  <div className="enterprise-owners__summaryCard">
                    <span>Avg Gap</span>
                    <strong>{toFixedDisplay(impactSummary?.avgGap, 2)}</strong>
                  </div>
                </div>
              </section>

              <section className="enterprise-owners__reportBlock">
                <h6 className="enterprise-owners__reportBlockTitle">Session Insights</h6>
                <div className="enterprise-owners__reportTableWrap">
                  <table className="enterprise-owners__reportTable">
                    <thead>
                      <tr>
                        <th>Session</th>
                        <th>Leader</th>
                        <th>Decision</th>
                        <th>Alignment</th>
                        <th>Impact Gap</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessionInsightRows.map((session) => (
                        <tr key={String(session.roomId || session._id)}>
                          <td>{session.session || String(session.roomId || "").slice(-8) || "—"}</td>
                          <td>{session.leader || "—"}</td>
                          <td>{toPercentDisplay(session.decision)}</td>
                          <td>{toPercentDisplay(session.alignment)}</td>
                          <td>{toFixedDisplay(session.impactGap, 2)}</td>
                        </tr>
                      ))}
                      {!performanceLoading && !sessionInsightRows.length ? (
                        <tr>
                          <td colSpan={5} className="enterprise-owners__reportEmpty">
                            No session insight records in this reporting window.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="enterprise-owners__reportBlock">
                <h6 className="enterprise-owners__reportBlockTitle">User Performance Table</h6>
                <div className="enterprise-owners__reportTableWrap">
                  <table className="enterprise-owners__reportTable">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Sessions</th>
                        <th>As Leader</th>
                        <th>As Peer</th>
                        <th>Decision</th>
                        <th>Alignment</th>
                        <th>Impact Gap</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userRows.map((participant, index) => {
                        const participantId = String(resolveParticipantUserId(participant) || "");
                        const rowKey = String(participantId || index);
                        const isSelected = Boolean(participantId) && participantId === selectedPerformanceUserId;
                        return (
                          <React.Fragment key={rowKey}>
                            <tr className={isSelected ? "enterprise-owners__drilldownSourceRow" : ""}>
                              <td>
                                <span>{participant.user || String(participantId || "").slice(-8)}</span>
                                {isSelected ? (
                                  <span className="enterprise-owners__drilldownOpenTag">Drilldown Open</span>
                                ) : null}
                              </td>
                              <td className="enterprise-owners__numCell">{participant.sessions || 0}</td>
                              <td className="enterprise-owners__numCell">{safeNumeric(participant.asLeader, 0)}</td>
                              <td className="enterprise-owners__numCell">{safeNumeric(participant.asPeer, 0)}</td>
                              <td className="enterprise-owners__numCell">{toPercentDisplay(participant.decision)}</td>
                              <td className="enterprise-owners__numCell">{toPercentDisplay(participant.alignment)}</td>
                              <td className="enterprise-owners__numCell">{toFixedDisplay(participant.impactGap, 2)}</td>
                              <td>
                                <button
                                  type="button"
                                  className="enterprise-owners__actionBtn enterprise-owners__actionBtn--primary"
                                  onClick={() => loadParticipantPerformance(participant)}
                                  disabled={!participantId}
                                >
                                  {isSelected ? "Opened" : "Drilldown"}
                                </button>
                              </td>
                            </tr>
                            {isSelected ? (
                              <tr className="enterprise-owners__drilldownDetailRow">
                                <td colSpan={8} className="enterprise-owners__drilldownDetailCell">
                                  {renderParticipantPerformanceReport()}
                                </td>
                              </tr>
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                      {!performanceLoading && !userRows.length ? (
                        <tr>
                          <td colSpan={8} className="enterprise-owners__reportEmpty">
                            No user comparison records in this reporting window.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="enterprise-owners__reportBlock">
                <h6 className="enterprise-owners__reportBlockTitle">Individual User Drilldown</h6>
                <p className="enterprise-owners__reportMuted mb-0">
                  Use the <strong>Drilldown</strong> action in the user table to open the full individual report.
                </p>
              </section>

              <section className="enterprise-owners__reportBlock">
                <h6 className="enterprise-owners__reportBlockTitle">Growth Trends</h6>
                <div className="enterprise-owners__reportTableWrap">
                  <table className="enterprise-owners__reportTable">
                    <thead>
                      <tr>
                        <th>Period / Week</th>
                        <th>Decision Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {growthRows.map((row) => (
                        <tr key={row.period}>
                          <td>{row.period}</td>
                          <td>{toPercentDisplay(row.decisionScore)}</td>
                        </tr>
                      ))}
                      {!growthRows.length ? (
                        <tr>
                          <td colSpan={2} className="enterprise-owners__reportEmpty">
                            No growth trend data available.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </section>
                </>
              ) : null}
            </section>
          </div>
        </div>
      </div>
    );
  };
  return (
    <div className={`enterprise-owners card p-3${detailsOpen ? " enterprise-owners--workspace-open" : ""}`}>
      <ToastContainer />

      {!detailsOpen ? (
        <>
          <div className="enterprise-owners__hero">
            <div className="enterprise-owners__header">
             
              <h2 className="enterprise-owners__title">Enterprise Calibration Overview</h2>

            </div>
            <div className="enterprise-owners__heroActions">
              <button
                type="button"
                className="enterprise-owners__btn enterprise-owners__btn--ghost"
                onClick={refreshTables}
                disabled={loadingTable}
              >
                {loadingTable ? "Refreshing..." : "Refresh Data"}
              </button>
              <button
                type="button"
                className="enterprise-owners__btn enterprise-owners__btn--primary"
                onClick={openCreateEnterprise}
              >
                Create Enterprise
              </button>
            </div>
          </div>

          <div className="enterprise-owners__stats">
            {summaryCards.map((card) => (
              <div className="enterprise-owners__statCard" key={card.label}>
                <span>{card.label}</span>
                <strong>{card.value}</strong>
              </div>
            ))}
          </div>

          <div className="enterprise-owners__table">
            <div className="enterprise-owners__tableHeadBlock">
              <div className="enterprise-owners__toolbarTop">
                <div className="enterprise-owners__results">
                  Showing {activeData.length} of {activeTotalCount} enterprises
                </div>
              </div>
              <div className="enterprise-owners__toolbar">
                <div className="enterprise-owners__searchWrap">
                  <input
                    className="form-control enterprise-owners__search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search Enterprises"
                    aria-label="Search enterprises"
                  />
                </div>

                <select
                  className="form-control enterprise-owners__select"
                  value={enterpriseStatusFilter}
                  onChange={(event) => setEnterpriseStatusFilter(event.target.value)}
                >
                  {ENTERPRISE_STATUS_FILTERS.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <select
                  className="form-control enterprise-owners__select"
                  value={enterprisePlanFilter}
                  onChange={(event) => setEnterprisePlanFilter(event.target.value)}
                >
                  {enterprisePlanOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <select
                  className="form-control enterprise-owners__select"
                  value={enterpriseDateFilter}
                  onChange={(event) => setEnterpriseDateFilter(event.target.value)}
                >
                  {ENTERPRISE_DATE_FILTERS.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  className="enterprise-owners__btn enterprise-owners__btn--ghost"
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                >
                  Clear Filters
                </button>
              </div>
            </div>
            <DataTable
              className="enterprise-owners__dataTable"
              columns={enterpriseColumns}
              data={activeData}
              progressPending={loadingTable}
              progressComponent={
                <div className="enterprise-owners__tableLoadingState" aria-live="polite">
                  <div className="enterprise-owners__tableLoadingText">
                    Refreshing enterprise records...
                  </div>
                  <TableSkeleton />
                </div>
              }
              customStyles={customStyles}
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 20, 50, 100]}
              paginationComponentOptions={paginationComponentOptions}
              highlightOnHover
              fixedHeader
              fixedHeaderScrollHeight="62vh"
              pointerOnHover
              onRowClicked={openDetails}
              noDataComponent={renderNoDataState}
            />
          </div>
        </>
      ) : null}

      <Modal show={enterpriseModalOpen} onHide={() => setEnterpriseModalOpen(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Start Enterprise Pilot</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form id="enterprise-create-form" onSubmit={submitEnterprise}>
            <div className="enterprise-owners__formLegend">
              Required fields are marked with <span>*</span>. Optional fields can be completed later.
            </div>
            <div className="form-row">
              <div className="form-group col-md-6">
                <label>
                  Enterprise Name <span className="enterprise-owners__requiredMark">*</span>
                </label>
                <input
                  className={`form-control ${enterpriseFormErrors.enterpriseName ? "is-invalid" : ""}`}
                  name="enterpriseName"
                  value={enterpriseForm.enterpriseName}
                  onChange={handleEnterpriseFormChange}
                  aria-invalid={Boolean(enterpriseFormErrors.enterpriseName)}
                />
                {enterpriseFormErrors.enterpriseName ? (
                  <small className="enterprise-owners__fieldError">{enterpriseFormErrors.enterpriseName}</small>
                ) : null}
              </div>
              <div className="form-group col-md-6">
                <label>
                  Company Name <span className="enterprise-owners__optionalMark">(Optional)</span>
                </label>
                <input
                  className="form-control"
                  name="companyName"
                  value={enterpriseForm.companyName}
                  onChange={handleEnterpriseFormChange}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group col-md-6">
                <label>
                  Company Domain <span className="enterprise-owners__requiredMark">*</span>
                </label>
                <input
                  className={`form-control ${enterpriseFormErrors.companyDomain ? "is-invalid" : ""}`}
                  name="companyDomain"
                  value={enterpriseForm.companyDomain}
                  onChange={handleEnterpriseFormChange}
                  aria-invalid={Boolean(enterpriseFormErrors.companyDomain)}
                />
                {enterpriseFormErrors.companyDomain ? (
                  <small className="enterprise-owners__fieldError">{enterpriseFormErrors.companyDomain}</small>
                ) : null}
              </div>
              <div className="form-group col-md-6">
                <label>
                  Enterprise Plan <span className="enterprise-owners__requiredMark">*</span>
                </label>
                <input
                  className={`form-control ${enterpriseFormErrors.planType ? "is-invalid" : ""}`}
                  name="planType"
                  value={enterpriseForm.planType}
                  onChange={handleEnterpriseFormChange}
                  aria-invalid={Boolean(enterpriseFormErrors.planType)}
                />
                {enterpriseFormErrors.planType ? (
                  <small className="enterprise-owners__fieldError">{enterpriseFormErrors.planType}</small>
                ) : null}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group col-md-4">
                <label>
                  Allocated Seats <span className="enterprise-owners__requiredMark">*</span>
                </label>
                <input
                  type="number"
                  min={1}
                  className={`form-control ${enterpriseFormErrors.seatCount ? "is-invalid" : ""}`}
                  name="seatCount"
                  value={enterpriseForm.seatCount}
                  onChange={handleEnterpriseFormChange}
                  aria-invalid={Boolean(enterpriseFormErrors.seatCount)}
                />
                {enterpriseFormErrors.seatCount ? (
                  <small className="enterprise-owners__fieldError">{enterpriseFormErrors.seatCount}</small>
                ) : null}
                <small className="enterprise-owners__fieldHint">
                  Seats define how many participants can join calibration sessions.
                </small>
              </div>
              <div className="form-group col-md-4">
                <label>
                  Calibration Start Date <span className="enterprise-owners__requiredMark">*</span>
                </label>
                <input
                  type="date"
                  className={`form-control ${enterpriseFormErrors.startDate ? "is-invalid" : ""}`}
                  name="startDate"
                  value={enterpriseForm.startDate}
                  onChange={handleEnterpriseFormChange}
                  aria-invalid={Boolean(enterpriseFormErrors.startDate)}
                />
                {enterpriseFormErrors.startDate ? (
                  <small className="enterprise-owners__fieldError">{enterpriseFormErrors.startDate}</small>
                ) : null}
              </div>
              <div className="form-group col-md-4">
                <label>
                  Calibration End Date <span className="enterprise-owners__requiredMark">*</span>
                </label>
                <input
                  type="date"
                  className={`form-control ${enterpriseFormErrors.endDate ? "is-invalid" : ""}`}
                  name="endDate"
                  value={enterpriseForm.endDate}
                  onChange={handleEnterpriseFormChange}
                  required
                  aria-invalid={Boolean(enterpriseFormErrors.endDate)}
                />
                {enterpriseFormErrors.endDate ? (
                  <small className="enterprise-owners__fieldError">{enterpriseFormErrors.endDate}</small>
                ) : null}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group col-md-4">
                <label>
                  Enterprise Lead Name <span className="enterprise-owners__optionalMark">(Optional)</span>
                </label>
                <input
                  className="form-control"
                  name="ownerName"
                  value={enterpriseForm.ownerName}
                  onChange={handleEnterpriseFormChange}
                />
              </div>
              <div className="form-group col-md-4">
                <label>
                  Enterprise Lead Email <span className="enterprise-owners__requiredMark">*</span>
                </label>
                <input
                  className={`form-control ${enterpriseFormErrors.ownerEmail ? "is-invalid" : ""}`}
                  name="ownerEmail"
                  value={enterpriseForm.ownerEmail}
                  onChange={handleEnterpriseFormChange}
                  aria-invalid={Boolean(enterpriseFormErrors.ownerEmail)}
                />
                {enterpriseFormErrors.ownerEmail ? (
                  <small className="enterprise-owners__fieldError">{enterpriseFormErrors.ownerEmail}</small>
                ) : null}
              </div>
              <div className="form-group col-md-4">
                <label>
                  Enterprise Lead Phone <span className="enterprise-owners__optionalMark">(Optional)</span>
                </label>
                <input
                  className="form-control"
                  name="ownerPhone"
                  value={enterpriseForm.ownerPhone}
                  onChange={handleEnterpriseFormChange}
                />
              </div>
              <div className="form-group col-md-4">
                <label>
                  Organization Name <span className="enterprise-owners__requiredMark">*</span>
                </label>
                <input
                  className={`form-control ${enterpriseFormErrors.ownerOrganizationName ? "is-invalid" : ""}`}
                  name="ownerOrganizationName"
                  value={enterpriseForm.ownerOrganizationName}
                  onChange={handleEnterpriseFormChange}
                  aria-invalid={Boolean(enterpriseFormErrors.ownerOrganizationName)}
                />
                {enterpriseFormErrors.ownerOrganizationName ? (
                  <small className="enterprise-owners__fieldError">{enterpriseFormErrors.ownerOrganizationName}</small>
                ) : null}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group col-md-4">
                <label>Industry <span className="enterprise-owners__optionalMark">(Optional)</span></label>
                <select className="form-control" name="ownerIndustry" value={enterpriseForm.ownerIndustry} onChange={handleEnterpriseFormChange}>
                  <option value="">Select industry</option>
                  {OWNER_INDUSTRY_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div className="form-group col-md-4">
                <label>Organization Size <span className="enterprise-owners__optionalMark">(Optional)</span></label>
                <select className="form-control" name="ownerOrganizationSize" value={enterpriseForm.ownerOrganizationSize} onChange={handleEnterpriseFormChange}>
                  <option value="">Select size</option>
                  {OWNER_ORGANIZATION_SIZE_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div className="form-group col-md-4">
                <label>Geographic Scope <span className="enterprise-owners__optionalMark">(Optional)</span></label>
                <select className="form-control" name="ownerGeographicScope" value={enterpriseForm.ownerGeographicScope} onChange={handleEnterpriseFormChange}>
                  <option value="">Select scope</option>
                  {OWNER_GEOGRAPHIC_SCOPE_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group col-md-4">
                <label>Owner Invite Default Role Mode</label>
                <select
                  className="form-control"
                  name="defaultRoleMode"
                  value={enterpriseForm.defaultRoleMode || "owner_and_participant"}
                  onChange={handleEnterpriseFormChange}
                >
                  <option value="owner_only">Owner only</option>
                  <option value="owner_and_participant">Owner + Participant</option>
                  {enterpriseForm.allowParticipantOnly ? (
                    <option value="participant_only">Participant only</option>
                  ) : null}
                </select>
              </div>
              <div className="form-group col-md-4 d-flex align-items-end">
                <label className="d-block mb-0">
                  <input
                    type="checkbox"
                    className="mr-2"
                    name="allowParticipantRole"
                    checked={Boolean(enterpriseForm.allowParticipantRole)}
                    onChange={handleEnterpriseFormChange}
                  />
                  Allow Owner + Participant invite choice
                </label>
              </div>
              <div className="form-group col-md-4 d-flex align-items-end">
                <label className="d-block mb-0">
                  <input
                    type="checkbox"
                    className="mr-2"
                    name="allowParticipantOnly"
                    checked={Boolean(enterpriseForm.allowParticipantOnly)}
                    onChange={handleEnterpriseFormChange}
                  />
                  Allow Participant-only invite choice
                </label>
              </div>
            </div>
            {SHOW_ADVANCED_ENTERPRISE_CONFIG ? (
            <>
            <div className="form-row">
              <div className="form-group col-md-6">
                <div className="enterprise-owners__configBlock">
                  <div className="enterprise-owners__configHeader">
                    <label className="mb-0">Feature Flags</label>
                    <span>Turn enterprise features on or off</span>
                  </div>
                  <div className="enterprise-owners__kvRows">
                  {(enterpriseForm.featureFlagRows || []).map((item, index) => (
                    <div key={`create-flag-${index}`} className="enterprise-owners__kvRow">
                      <input
                        className="form-control enterprise-owners__kvInput"
                        placeholder="Feature name (example: challenge_access)"
                        value={item.key}
                        onChange={(event) =>
                          updateFeatureFlagRow("change", index, "key", event.target.value)
                        }
                      />
                      <select
                        className="form-control enterprise-owners__kvInput"
                        value={item.enabled ? "enabled" : "disabled"}
                        onChange={(event) =>
                          updateFeatureFlagRow(
                            "change",
                            index,
                            "enabled",
                            event.target.value === "enabled"
                          )
                        }
                      >
                        <option value="enabled">Enabled</option>
                        <option value="disabled">Disabled</option>
                      </select>
                      <button
                        type="button"
                        className="enterprise-owners__uiBtn enterprise-owners__uiBtn--neutral enterprise-owners__kvRemove"
                        onClick={() => updateFeatureFlagRow("remove", index)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="enterprise-owners__uiBtn enterprise-owners__uiBtn--accent"
                    onClick={() => updateFeatureFlagRow("add")}
                  >
                    Add Feature Flag
                  </button>
                  </div>
                </div>
              </div>

              <div className="form-group col-md-6">
                <div className="enterprise-owners__configBlock">
                  <div className="enterprise-owners__configHeader">
                    <label className="mb-0">Usage Limits</label>
                    <span>Set enterprise usage caps</span>
                  </div>
                  <div className="enterprise-owners__kvRows">
                  {(enterpriseForm.usageLimitRows || []).map((item, index) => (
                    <div key={`create-limit-${index}`} className="enterprise-owners__kvRow">
                      <input
                        className="form-control enterprise-owners__kvInput"
                        placeholder="Limit name (example: monthly_challenges)"
                        value={item.key}
                        onChange={(event) =>
                          updateUsageLimitRow("change", index, "key", event.target.value)
                        }
                      />
                      <input
                        className="form-control enterprise-owners__kvInput"
                        placeholder="Value (number or text)"
                        value={item.value}
                        onChange={(event) =>
                          updateUsageLimitRow("change", index, "value", event.target.value)
                        }
                      />
                      <button
                        type="button"
                        className="enterprise-owners__uiBtn enterprise-owners__uiBtn--neutral enterprise-owners__kvRemove"
                        onClick={() => updateUsageLimitRow("remove", index)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="enterprise-owners__uiBtn enterprise-owners__uiBtn--accent"
                    onClick={() => updateUsageLimitRow("add")}
                  >
                    Add Usage Limit
                  </button>
                  </div>
                </div>
              </div>
            </div>
            </>
            ) : null}
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="enterprise-owners__uiBtn enterprise-owners__uiBtn--neutral"
            onClick={() => setEnterpriseModalOpen(false)}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="enterprise-create-form"
            className="enterprise-owners__uiBtn enterprise-owners__uiBtn--primary"
            disabled={enterpriseSubmitting}
          >
            {enterpriseSubmitting ? "Creating..." : "Create Enterprise"}
          </button>
        </Modal.Footer>
      </Modal>

      {detailsOpen ? (
        <section
          ref={detailsWorkspaceRef}
          className="enterprise-owners__workspace"
          aria-label="Enterprise Calibration Control Panel"
        >
          <div className="enterprise-owners__workspaceHeader">
            <div>
              <div className="enterprise-owners__workspaceEyebrow">
                Enterprise Calibration Control Panel
              </div>
              <h3 className="enterprise-owners__workspaceTitle">
                {detailsEnterprise?.name || "Enterprise"}
              </h3>
              <p className="enterprise-owners__workspaceSubtitle">
                Configure lifecycle controls, Enterprise Lead permissions, participants,
                and operational logs in a structured workspace.
              </p>
            </div>
            <div className="enterprise-owners__workspaceActions">
              <button
                type="button"
                className="enterprise-owners__uiBtn enterprise-owners__uiBtn--neutral"
                onClick={() => setDetailsOpen(false)}
              >
                Back to Enterprise List
              </button>
              <button
                type="button"
                className="enterprise-owners__uiBtn enterprise-owners__uiBtn--primary"
                onClick={saveEnterpriseChanges}
                disabled={detailsLoading}
              >
                {detailsLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>

          <div className="enterprise-owners__workspaceBody">
            {detailsLoading ? (
              renderDetailsLoading()
            ) : (
              <>
                <div className="enterprise-owners__tabs enterprise-owners__tabs--panel mb-3">
                  {DETAIL_TABS.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      className={`enterprise-owners__tabBtn ${detailTab === tab.key ? "is-active" : ""}`}
                      onClick={() => setDetailTab(tab.key)}
                      aria-current={detailTab === tab.key ? "page" : undefined}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {detailTab === "overview" ? renderOverviewTab() : null}
                {detailTab === "users" ? renderUsersTab() : null}
                {detailTab === "verifications" ? renderVerificationsTab() : null}
                {detailTab === "performance" ? renderPerformanceTab() : null}
                {detailTab === "logs" ? renderLogsTab() : null}
              </>
            )}
          </div>

          <div className="enterprise-owners__workspaceFooter">
            <button
              type="button"
              className="enterprise-owners__uiBtn enterprise-owners__uiBtn--neutral"
              onClick={() => setDetailsOpen(false)}
            >
              Back to Enterprise List
            </button>
            <button
              type="button"
              className="enterprise-owners__uiBtn enterprise-owners__uiBtn--primary"
              onClick={saveEnterpriseChanges}
              disabled={detailsLoading}
            >
              {detailsLoading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </section>
      ) : null}

      <Modal
        show={Boolean(verificationPreview?.url)}
        onHide={() => setVerificationPreview(null)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{verificationPreview?.label || "Verification Image"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {verificationPreview?.url ? (
            <img
              src={verificationPreview.url}
              alt={verificationPreview?.label || "Verification"}
              style={{
                width: "100%",
                maxHeight: "75vh",
                objectFit: "contain",
                border: "1px solid #d7dce5",
                borderRadius: "10px",
                background: "#f8fafc",
              }}
            />
          ) : null}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default EnterpriseOwners;
